import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  Card,
  Box,
  Divider,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import CTLNotification from "application/components/CTLNotification";
import AuthenticationService from "application/modules/authentication/AuthenticationService";
import NotificationManager from "globals/websocket/WebSocket";
import { useMaterialUIController } from "context";
import { setDispatcherEvent } from "context";
import { setRTCEvent } from "context";
import V3MediaPTT from "libs/V3MediaPTT";
import AudioRecorder from "../AudioRecorder";
import { telemetry } from "globals/endpoints";
import { setClickToCall } from "context";
import { setClickToVideoCall } from "context";
import { setAmbientMode } from "context";
import emergencyCall from "assets/sound/emergency.wav";
import floor_received from "assets/sound/floor-received.wav";
import floor_release from "assets/sound/floor-releas.wav";
import floor_taken from "assets/sound/floor-taken.wav";
import incomingCall from "assets/sound/ringing.wav";
import dial_call from "assets/sound/ringback-tone.wav";
import beep from "assets/sound/beep.wav";
import PhoneHeader from "./PhoneHeader";
import VideoPanel from "./VideoPanel";
import DialPad from "./DialPad";
import CallControls from "./CallControls";
import CallListDialog from "./CallListDialog";
import useVideoPanel from "./useVideoPanel";
import { setDragAndDropEvent } from "context";
import MDButton from "components/MDButton";
import Draggable from "react-draggable";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import { setActiveCalls } from "context";



const DraggablePaper = forwardRef(function DraggablePaper(props, ref) {
  const nodeRef = useRef(null);

  return (
    <Draggable
      nodeRef={nodeRef}
      handle=".draggable-video-card-title"
      cancel={'[class*="MuiDialogContent-root"], [class*="MuiIconButton-root"]'}
    >
      <Paper
        ref={(instance) => {
          nodeRef.current = instance;

          if (typeof ref === "function") {
            ref(instance);
          } else if (ref) {
            ref.current = instance;
          }
        }}
        {...props}
      />
    </Draggable>
  );
});

const WebRTCPhone = forwardRef(function WebRTCPhone(
  { compactMode = false, onStatusChange, allowVideoPopup = true, contacts = [] },
  ref,
) {
  const pttRef = useRef(null);
  const audioPlayingRef = useRef(null);
  const ambientRingRef = useRef(null);
  const dialRingRef = useRef(null);
  const localStreamRef = useRef(null);
  const talkStartRef = useRef(null);
  const timerThreadRef = useRef(null);
  const lastPlayedUserRef = useRef(null);
  const liveGroupCall = useRef(null);
  const callTimeoutRef = useRef(null);
  const lastQueuedRingRoomRef = useRef(null);
  const autoJoinTimeoutRef = useRef(null);
  const isJoiningRef = useRef(false);
  const lastChatMessageRequestRef = useRef(new Map());
  const lastDispatcherEventRef = useRef({
    signature: "",
    handledAt: 0,
  });

  const defaultTime = 60; // seconds - timeout for call connection

  const AUTO_ANSWER_AFTER_RING_MS =  0;
  const VIDEO_AUTO_ANSWER_AFTER_RING_MS = 20;
  const SOS_AUTO_ANSWER_AFTER_RING_MS = 20;

  const callStateRef = useRef({
    roomId: "",
    streamId: "",
    publishedStreamId: "",
    requestId: "",
    callKind: "",
    isClearing: false,
    isDisconnecting: false,
    isStreamAckSent: false,
    inCall: false,
    isDialing: false,
    isIncoming: false,
    isBargeIn: false,
    bargeInSentFrom: "",
    bargeInSentTo: "",
    remoteUserStreamId:"",
  });

  const audioRefs = {
    emergency: emergencyCall,
    floor_received,
    floor_release,
    floor_taken,
    incomingCall,
    dial_call,
    beep,
  };

    const isWebView21 = () => {
      return (
        /wv|WebView|Edge\/\d+/i.test(navigator.userAgent) ||
        window.isWebView21 === true
      );
    };

    function generateUUID() {
      return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
        /[xy]/g,
        function (c) {
          const r = (Math.random() * 16) | 0;
          const v = c === "x" ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        },
      );
    }

  const domain = AuthenticationService.getXmppPrimaryDomain();
  const serverUrl =
    domain && domain.trim() !== ""
      ? `${domain}/LiveApp/websocket`
      : "wss://conf.coraltele.com:5443/LiveApp/websocket";

  const postChatMessageSafe = (payload, dedupeMs = 1000) => {
    if (!payload || typeof payload !== "object") {
      return;
    }

    const requestKey = [
      payload.messageType || "",
      payload.kind || "",
      payload.sender || "",
      payload.target || "",
      payload.content || "",
    ].join("|");

    const now = Date.now();
    const lastSentAt = lastChatMessageRequestRef.current.get(requestKey) || 0;

    if (now - lastSentAt < dedupeMs) {
      console.warn("⚠️ [API] Skipping duplicate chat/message request", {
        target: payload.target,
        messageType: payload.messageType,
      });
      return;
    }

    lastChatMessageRequestRef.current.set(requestKey, now);

    // Keep dedupe map bounded by removing stale entries.
    if (lastChatMessageRequestRef.current.size > 200) {
      const expiryCutoff = now - 15000;
      for (const [key, timestamp] of lastChatMessageRequestRef.current) {
        if (timestamp < expiryCutoff) {
          lastChatMessageRequestRef.current.delete(key);
        }
      }
    }

    telemetry.post("chat/message", payload).catch((error) => {
      console.warn("⚠️ [API] chat/message failed", {
        status: error?.response?.status,
        message: error?.message,
        target: payload.target,
      });
    });
  };

  const clearCallTimeout = () => {
    if (callTimeoutRef.current) {
      clearTimeout(callTimeoutRef.current);
      callTimeoutRef.current = null;
      console.log("⏱️ [Timeout] Call timeout cleared");
    }
  };

  const startCallTimeout = (isAmbient = false) => {
    clearCallTimeout();

    console.log(
      `⏱️ [Timeout] Starting ${defaultTime}s timeout for ${
        isAmbient ? "ambient" : "call"
      } connection...`,
    );

    callTimeoutRef.current = setTimeout(() => {
      console.log(
        `⏰ [Timeout] ${defaultTime}s elapsed without event - auto-disconnecting...`,
      );

      setNotify({
        isOpen: true,
        type: "error",
        pagename: "Phone",
        status: "Call failed to connect. Please try again.",
      });

      if (isAmbient) {
        disconnectAmbientCall();
      } else {
        disconnectCall();
      }

      cleanupP2P();
    }, defaultTime * 1000);
  };

  const [connectionState, setConnectionState] = useState("idle");
  const [remoteStreams, setRemoteStreams] = useState([]);
  const [isMuted, setIsMuted] = useState(false);
  const [pttStatus, setPttStatus] = useState("PTT Available");
  const [dialValue, setDialValue] = useState("");
  const [callTimer, setCallTimer] = useState("00:00:00");
  const [isVideoEnabled, setIsVideoEnabled] = useState(false);
  const department = AuthenticationService.getDepartmentList();
  const [callType, setCallType] = useState({
    isP2P: false,
    isSOS: false,
    isAmbient: false,
    isPTTDisabled: false,
    isVideoCall: false,
  });
  const callTypeRef = useRef(callType);

  useEffect(() => {
    callTypeRef.current = callType;
  }, [callType]);

  const [announcement, setAnnouncement] = useState({
    isActive: false,
    department: "",
  });

  const [callList, setCallList] = useState([]);
  const callListRef = useRef(callList);
  const disconnectStateTimeoutRef = useRef(null);
  const [isCallListOpen, setIsCallListOpen] = useState(false);
  const [callEnded, setCallEnded] = useState(false);
  const [isVideoMaximized, setIsVideoMaximized] = useState(false);

  useEffect(() => {
    callListRef.current = callList;
  }, [callList]);

  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
    pagename: "",
    status: "",
  });

  const wsRef = useRef(null);
  const pcRef = useRef(null);
  const dataChannelRef = useRef(null);

  const [controller, dispatch] = useMaterialUIController();
  const {
    RTCEvent,
    darkMode,
    clickToCall,
    clickToVideoCall,
    dispatcherEvent,
    ambientMode,
    activeCalls,  
  } = controller;

  const {
    localVideoRef,
    videoPanelHeight,
    startVideoResize,
    attachLocalPreview,
    clearLocalPreview,
  } = useVideoPanel(localStreamRef);

  const normalizeDialLookupValue = (value) => {
    const rawValue = String(value || "").trim();

    if (!rawValue) {
      return "";
    }

    const valueWithoutDomain = rawValue.includes("@")
      ? rawValue.split("@")[0]
      : rawValue;
    const numberLike = valueWithoutDomain.replace(/[^\d*#]/g, "");

    return numberLike || valueWithoutDomain.toLowerCase();
  };

  const findDialContact = (target) => {
    const targetKey = normalizeDialLookupValue(target);

    if (!targetKey || !Array.isArray(contacts) || contacts.length === 0) {
      return null;
    }

    for (const contact of contacts) {
      const candidateValues = [
        contact?.offEXtn,
        contact?.extension,
        contact?.number,
        contact?.phone,
        contact?.mobile,
        contact?.extn,
        contact?.userName,
        contact?.userId,
      ];

      const isMatched = candidateValues.some(
        (candidate) => normalizeDialLookupValue(candidate) === targetKey,
      );

      if (isMatched) {
        return contact;
      }
    }

    return null;
  };

  const shouldPostWebViewCallEvent = (target) => {
    if (!isWebView21()) {
      return false;
    }
     return true; 
    };

  const postWebViewCallEvent = (target, isVideoCall) => {
    const data = {
      number: String(target || "").trim(),
      isVideoCall: Boolean(isVideoCall),
    };

    console.log(
      "📱 Detected WebView 2.1 environment, sending click-to-call data to parent window",
      data,
    );

    window?.chrome?.webview?.postMessage(
      JSON.stringify({
        eventType: "Call",
        data,
      }),
    );
  };

  const shouldHandleDispatcherEvent = (event) => {
    if (!event || typeof event !== "object") {
      return false;
    }

    const signature = [
      event.messageType,
      event.message,
      event.messageSentBy,
      event.activeGroup,
      event.floorWith,
      event.subType,
      event.appName,
    ].join("|");
    const now = Date.now();

    if (
      signature &&
      lastDispatcherEventRef.current.signature === signature &&
      now - lastDispatcherEventRef.current.handledAt < 500
    ) {
      return false;
    }

    lastDispatcherEventRef.current = {
      signature,
      handledAt: now,
    };

    return true;
  };

  useEffect(() => {
    if (typeof onStatusChange !== "function") {
      return;
    }

    onStatusChange({
      connectionState,
      pttStatus,
      callActive: Boolean(callStateRef.current.roomId),
      activeCallType: callStateRef.current.callKind || "",
      callTimer,
      isVideoCall: callType.isVideoCall,
      waitingCalls: callList.length,
      isMuted,
      isVideoEnabled,
      canToggleVideo: callType.isP2P && callType.isVideoCall,
      isP2P: callType.isP2P,
      isAmbient: callType.isAmbient,
      callList,
      remoteStreams,
      callStreamId: callStateRef.current.streamId,
      isBargeIn: callStateRef.current.isBargeIn,
      bargeInSentFrom: callStateRef.current.bargeInSentFrom,
      bargeInSentTo: callStateRef.current.bargeInSentTo,
    });
  }, [
    onStatusChange,
    connectionState,
    pttStatus,
    callTimer,
    callType.isVideoCall,
    callType.isP2P,
    callType.isSOS,
    callType.isAmbient,
    callList.length,
    callList,
    remoteStreams,
    isMuted,
    isVideoEnabled,
    callEnded,
  ]);

  useEffect(() => {
    const extensionNumber = AuthenticationService.getExtensionNumber();
 
    if (!extensionNumber) {
      return undefined;
    }

    const handleMCXSocketEvent = (message) => {
      if (!message?.body) {
        return;
      }

      try {
        const mcxEvent = JSON.parse(message.body);
        setDispatcherEvent(dispatch, mcxEvent);
      } catch (error) {
        console.warn("Failed to parse MCX event in WebRTCPhone:", error);
      }
    };

    // Subscribe to user's personal MCX topic
    const userMcxCallbackId = `webrtc-phone-mcx-${extensionNumber}`;
    NotificationManager.receiveMessageForMCX(
      `mcx${extensionNumber}`,
      handleMCXSocketEvent,
      userMcxCallbackId,
    );

    // Subscribe to all department conference topics
    const departments = AuthenticationService.getDepartmentList();
    const conferenceCallbackIds = [];
    
    if (departments && Array.isArray(departments)) {
      departments.forEach((dept) => {
        if (dept.extension) {
          const conferenceCallbackId = `webrtc-phone-conference-${dept.extension}`;
          conferenceCallbackIds.push(conferenceCallbackId);
          
          NotificationManager.receiveMessageForMCX(
            `/app/v2/messaging/topic/conference-${dept.extension}`,
            handleMCXSocketEvent,
            conferenceCallbackId,
          );
        }
      });
    }

    return () => {
      // Cleanup user MCX subscription
      AuthenticationService.mcxCallBacks.delete(userMcxCallbackId);
      
      // Cleanup all conference subscriptions
      conferenceCallbackIds.forEach((callbackId) => {
        AuthenticationService.mcxCallBacks.delete(callbackId);
      });
    };
  }, [dispatch]);

  useEffect(() => {
    if (!allowVideoPopup) {
      if (isVideoMaximized) {
        setIsVideoMaximized(false);
      }
      return;
    }

    if (
      callType.isVideoCall &&
      connectionState === "connected" &&
      Boolean(callStateRef.current.roomId) &&
      !isVideoMaximized
    ) {
      setIsVideoMaximized(true);
    }
  }, [
    callType.isVideoCall,
    connectionState,
    isVideoMaximized,
    allowVideoPopup,
  ]);

  useEffect(() => {
    if (callList.length === 0) {
      setIsCallListOpen(false);
    }
  }, [callList]);

  useEffect(() => {
    if (callList.length === 0) {
      lastQueuedRingRoomRef.current = null;
      if (autoJoinTimeoutRef.current) {
        clearTimeout(autoJoinTimeoutRef.current);
        autoJoinTimeoutRef.current = null;
      }
      console.log(
        "📭 [AutoJoin] Call list is empty, resetting auto-join tracking",
      );
      return;
    }

    const isCallActive =
      Boolean(callStateRef.current.roomId) || callStateRef.current.inCall;

    console.log("📞 [AutoJoin] Checking auto-join condition:", {
      compactMode,
      callListLength: callList.length,
      isCallActive,
      roomId: callStateRef.current.roomId,
      inCall: callStateRef.current.inCall,
      firstCaller: callList[0]?.sender,
      lastTracked: lastQueuedRingRoomRef.current,
    });

    if (isCallActive) {
      if (autoJoinTimeoutRef.current) {
        clearTimeout(autoJoinTimeoutRef.current);
        autoJoinTimeoutRef.current = null;
      }
      console.log("📞 [AutoJoin] Call is already active, skipping auto-join");
      return;
    }

    // // Skip if a manual join is already in progress (e.g. user clicked a specific call to answer).
    // if (isJoiningRef.current) {
    //   if (autoJoinTimeoutRef.current) {
    //     clearTimeout(autoJoinTimeoutRef.current);
    //     autoJoinTimeoutRef.current = null;
    //   }
    //   console.log("📞 [AutoJoin] Manual join in progress, skipping auto-join");
    //   return;
    // }

    const nextWaitingCall = callList[0];
    if (!nextWaitingCall) {
      if (autoJoinTimeoutRef.current) {
        clearTimeout(autoJoinTimeoutRef.current);
        autoJoinTimeoutRef.current = null;
      }
      console.log("📞 [AutoJoin] No waiting calls found");
      return;
    }

    console.log(
      "📞 [AutoJoin] Triggering auto-answer for:",
      nextWaitingCall.sender,
    );

    // Clear any pending auto-join to prevent rapid successive joins
    if (autoJoinTimeoutRef.current) {
      clearTimeout(autoJoinTimeoutRef.current);
      console.log("📞 [AutoJoin] Cleared previous pending timeout");
    }

    const queuedAt = Number(nextWaitingCall?.queuedAt || Date.now());
    const elapsedMs = Math.max(0, Date.now() - queuedAt);
    const targetDelayMs = nextWaitingCall.isSOS
      ? SOS_AUTO_ANSWER_AFTER_RING_MS
      : nextWaitingCall.isVideo
        ? VIDEO_AUTO_ANSWER_AFTER_RING_MS
        : AUTO_ANSWER_AFTER_RING_MS;
    const waitMs = Math.max(0, targetDelayMs - elapsedMs);

    // Keep the ring very short and answer quickly for smoother dispatch flows.
    autoJoinTimeoutRef.current = setTimeout(() => {
      // Double-check: skip if a join is already in progress or a call became active.
      if (isJoiningRef.current) {
        console.warn("⚠️ [AutoJoin] Join already in progress at fire time, skipping");
        return;
      }
      if (Boolean(callStateRef.current.roomId) || callStateRef.current.inCall) {
        console.warn("⚠️ [AutoJoin] Call became active between schedule and fire, skipping");
        return;
      }
      console.log(
        "✅ [AutoJoin] Executing auto-answer for:",
        nextWaitingCall.sender,
      );
      dialWaitingCall(nextWaitingCall);
    }, waitMs);
  }, [
    AUTO_ANSWER_AFTER_RING_MS,
    VIDEO_AUTO_ANSWER_AFTER_RING_MS,
    SOS_AUTO_ANSWER_AFTER_RING_MS,
    compactMode,
    callList,
    callType.isP2P,
    callType.isSOS,
    callType.isAmbient,
    connectionState,
    callEnded,
  ]);

  useEffect(() => {
    const handleBeforeUnload = async (e) => {
  
      if (callStateRef.current.roomId) {
        console.log("📞 [Cleanup] Active call detected:", {
          roomId: callStateRef.current.roomId,
          streamId: callStateRef.current.streamId,
          isP2P: callType.isP2P,
          isSOS: callType.isSOS,
          isAmbient: callType.isAmbient,
        });

        // Send floor release for group calls
        sendPttMessage("floor-release", callStateRef.current.roomId);

        // Disconnect based on call type
        if (callType.isP2P || callType.isSOS) {
          disconnectCall(
            callStateRef.current.streamId,
            callStateRef.current.roomId,
          );
        } else if (callType.isAmbient) {
          disconnectAmbientCall();
        }

        // Close PTT connection
        try {
          if (pttRef.current) {
            await pttRef.current.close();
            console.log("✅ [Cleanup] PTT connection closed");
          }
        } catch (err) {
          console.warn("⚠️ [Cleanup] PTT close error:", err);
        }

        // Stop local media
        stopLocalMedia();

        console.log("✅ [Cleanup] Active call disconnected successfully");
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("unload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("unload", handleBeforeUnload);

      if (autoJoinTimeoutRef.current) {
        clearTimeout(autoJoinTimeoutRef.current);
      }
      if (disconnectStateTimeoutRef.current) {
        clearTimeout(disconnectStateTimeoutRef.current);
        disconnectStateTimeoutRef.current = null;
      }

      // Cleanup on component unmount (logout case)
      console.log("🚪 [Cleanup] Component unmounting - cleaning up...");
      if (callStateRef.current.roomId) {
        if (callType.isP2P || callType.isSOS) {
          disconnectCall(
            callStateRef.current.streamId,
            callStateRef.current.roomId,
          );
        } else if (callType.isAmbient) {
          disconnectAmbientCall();
        }
        try {
          pttRef.current?.close();
        } catch (e) {
          console.warn("⚠️ [Cleanup] Unmount PTT close error:", e);
        }
        stopLocalMedia();
      }
    };
  }, [callType.isP2P, callType.isSOS, callType.isAmbient]);

  const isOwnPublishedStream = (sId, stream) => {
    const streamKey = String(sId ?? "");
    const publishedStreamId = String(callStateRef.current.publishedStreamId || "");

    if (
      publishedStreamId &&
      (streamKey === publishedStreamId ||
        streamKey === `play_${publishedStreamId}` ||
        `play_${streamKey}` === publishedStreamId)
    ) {
      return true;
    }

    if (!stream || typeof stream.getTracks !== "function") {
      return false;
    }

    const localTrackIds = new Set(
      (localStreamRef.current?.getTracks?.() || []).map((localTrack) => localTrack.id),
    );

    if (localTrackIds.size === 0) {
      return false;
    }

    return stream.getTracks().some((remoteTrack) => localTrackIds.has(remoteTrack.id));
  };

  const createRemoteStreamItem = (sId, stream) => ({
    id: sId,
    stream,
    hasVideo: (stream?.getVideoTracks?.() || []).length > 0,
    hasAudio: (stream?.getAudioTracks?.() || []).length > 0,
    isLocalEcho: isOwnPublishedStream(sId, stream),
  });

  const addRemoteStream = (sId, stream) => {
    if (!stream || isOwnPublishedStream(sId, stream)) return;

    // For outgoing P2P calls the ACK-based timer may never fire (server routing);
    // use the first remote stream as the reliable "call answered" signal instead.
    if (
      callStateRef.current.inCall &&
      callStateRef.current.callKind !== "Group" &&
      !talkStartRef.current
    ) {
      console.log("⏱️ [Timer] Remote stream received — starting call timer");
      startTimer();
    }

    setRemoteStreams((prev) => {
      const newItem = createRemoteStreamItem(sId, stream);
      const existingIndex = prev.findIndex((s) => s.id === sId);

      if (newItem.isLocalEcho) {
        return prev.filter((s) => s.id !== sId);
      }

      if (existingIndex >= 0) {
        const existing = prev[existingIndex];

        if (
          existing.stream === stream &&
          existing.hasVideo === newItem.hasVideo &&
          existing.hasAudio === newItem.hasAudio
        ) {
          return prev;
        }

        const updated = [...prev];
        updated[existingIndex] = { ...existing, ...newItem };
        return updated;
      }

      return [...prev, newItem];
    });
  };

  const addRemoteTrack = (sId, track) => {
    if (!track || isOwnPublishedStream(sId)) return;

    setRemoteStreams((prev) => {
      const existing = prev.find((s) => s.id === sId);
      const stream = existing?.stream || new MediaStream();
      const existingTracks = stream.getTracks?.() || [];
      const hasTrackAlready = existingTracks.some(
        (candidate) => candidate.id === track.id,
      );

      if (!hasTrackAlready) {
        const sameKindTrack = existingTracks.find(
          (candidate) => candidate.kind === track.kind,
        );

        if (sameKindTrack && sameKindTrack.id !== track.id) {
          try {
            stream.removeTrack(sameKindTrack);
          } catch (error) {
            console.warn("Failed to replace existing remote track:", error);
          }
        }

        try {
          stream.addTrack(track);
        } catch (error) {
          console.warn("Failed to add remote track:", error);
          return prev;
        }
      }

      const newItem = createRemoteStreamItem(sId, stream);

      if (newItem.isLocalEcho) {
        return prev.filter((s) => s.id !== sId);
      }

      if (existing) {
        return prev.map((s) => (s.id === sId ? { ...s, ...newItem } : s));
      }

      return [...prev, newItem];
    });

    track.onended = null;
    track.addEventListener("ended", () => {
      setRemoteStreams((prev) =>
        prev
          .map((streamItem) => {
            if (streamItem.id !== sId || !streamItem.stream) {
              return streamItem;
            }

            try {
              streamItem.stream.removeTrack(track);
            } catch {}

            return createRemoteStreamItem(sId, streamItem.stream);
          })
          .filter(
            (streamItem) =>
              streamItem.id !== sId ||
              ((streamItem.stream?.getTracks?.() || []).length > 0 &&
                !streamItem.isLocalEcho),
          ),
      );
    });
  };

  const handleRemoteTrack = ({
    instanceId,
    track,
    stream,
    streamId: sid,
    removed,
  }) => {
    if (
      instanceId &&
      pttRef.current &&
      instanceId !== pttRef.current.instanceId
    ) {
      console.warn("Ignoring stale PTT instance track:", instanceId);
      return;
    }

    if (sid === callStateRef.current.streamId) {
      console.warn("Ignoring own stream:", sid);
      return;
    }

    if (removed) {
      removeRemoteStream(sid);
      return;
    }

    if (stream) {
      addRemoteStream(sid, stream);
    } else if (track) {
      if (track.readyState !== "live") return;
      addRemoteTrack(sid, track);
    }
  };

  const removeRemoteStream = (sId) => {
    const id = String(sId ?? "");
    if (!id) return;

    const mirrorId = id.startsWith("play_") ? id.slice(5) : `play_${id}`;
    setRemoteStreams((prev) =>
      prev.filter((s) => s.id !== id && s.id !== mirrorId),
    );
  };

  useEffect(() => {
    if(department) {
      department.map((dept) => sendPttMessage("join-session", dept.extension));
    }
  }, []);

  useEffect(() => {
    const handleGroupCall = async () => {
      if (!RTCEvent || !RTCEvent.data || RTCEvent.type !== "GroupCall") return;

      if (RTCEvent.value === "LIVE GROUP CALL ANNOUNCEMENT") {
        liveGroupCall.current = true;
      }

      await clearRunningCall();

      try {
        callStateRef.current.roomId = RTCEvent.data;
        callStateRef.current.streamId = RTCEvent.value;
        callStateRef.current.callKind = "Group";
        callStateRef.current.inCall = true;
        const sid = `stream${Date.now()}`;
        callStateRef.current.publishedStreamId = sid;
        setIsMuted(true); // Start group call muted by default

        // Get local audio stream for group call
        console.log("🎤 [GroupCall] Requesting microphone access...");
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
          video: false,
        });

        // Disable audio tracks by default (muted)
        stream.getAudioTracks().forEach((track) => {
          track.enabled = false;
          console.log("🔇 [GroupCall] Audio track disabled (muted by default)");
        });

        localStreamRef.current = stream;

        const ptt = new V3MediaPTT({
          websocketUrl: serverUrl,
          streamId: sid,
          roomId: callStateRef.current.roomId,
          onRemoteTrack: handleRemoteTrack,
          onInitialized: () => {
            setConnectionState("connected");
            console.log(
              "✅ Group call connected:",
              callStateRef.current.roomId,
            );
          },
          onStateChange: handlePttState,
          onError: (e) => {
            console.error("❌ Group call error:", e);
            cleanupP2P();
          },
        });

        pttRef.current = ptt;
        await ptt.playPublish(stream, callStateRef.current.roomId, false);
        sendPttMessage("join-session", callStateRef.current.roomId);
      } catch (e) {
        console.error("❌ Group call init error:", e);
      }

      return () => {
        try {
          pttRef.current?.close();
        } catch (e) {
          console.warn("PTT cleanup error:", e);
        }
        pttRef.current = null;
        liveGroupCall.current = null;
        setRTCEvent(dispatch, null);
      };
    };

    const handleAnnouncement = () => {
      if (RTCEvent && RTCEvent.type === "announcement") {
        setAnnouncement({
          isActive: true,
          department: RTCEvent.value,
        });
      }
    };

    handleGroupCall();
    handleAnnouncement();
  }, [RTCEvent, dispatch, serverUrl]);

  const buildPayload = (messageType, roomId) => ({
    timestamp: Date.now(),
    messageType,
    userName: AuthenticationService.getUserName(),
    activeGroup: roomId,
    defaultGroup: roomId,
    groupCallActive: true,
    privateCallActive: false,
    callType: "group",
    destinationIP: "voip.coraltele.com",
    destinationPort: 8976,
    floorWith: null,
    message: null,
    messageSentBy: null,
    messageSubType: null,
    callMode: "VoIP",
  });

  const sendPttMessage = (msgType, roomId) => {
    const payload = buildPayload(msgType, roomId);
    NotificationManager.sendMCX(payload);
  };

  const stopPreview = () => {
    try {
      stopLocalMedia();
    } catch (e) {
      console.warn("Preview stop error:", e);
    }
  };

  const stopLocalMedia = () => {
    if (localStreamRef.current) {
      try {
        localStreamRef.current.getTracks().forEach((track) => track.stop());
      } catch {}
      localStreamRef.current = null;
    }
    clearLocalPreview();
  };

  const playSound = (key) => {
    if (!audioRefs[key]) return;

    if (audioPlayingRef.current) {
      try {
        audioPlayingRef.current.pause();
        audioPlayingRef.current.currentTime = 0;
      } catch (e) {}
    }

    const audio = new Audio(audioRefs[key]);
    audio.loop = false;
    audioPlayingRef.current = audio;

    audio.play().catch((err) => {
      console.warn("Audio play prevented:", err);
    });

    audio.onended = () => {
      audioPlayingRef.current = null;
    };
  };

  const playAmbientRing = () => {
    console.log("🔔 [Ambient] Starting emergency ring loop...");
    stopAmbientRing(); // Stop any existing ambient ring

    if (!audioRefs["emergency"]) return;

    const audio = new Audio(audioRefs["emergency"]);
    audio.loop = true; // Loop continuously
    ambientRingRef.current = audio;

    audio.play().catch((err) => {
      console.warn("⚠️ [Ambient] Ring play prevented:", err);
    });
  };

  const stopAmbientRing = () => {
    if (ambientRingRef.current) {
      console.log("🔕 [Ambient] Stopping emergency ring loop");
      try {
        ambientRingRef.current.pause();
        ambientRingRef.current.currentTime = 0;
      } catch (e) {}
      ambientRingRef.current = null;
    }
  };

  const playDialRing = () => {
    console.log("📞 [Dial] Starting dial ring loop...");
    stopDialRing(); // Stop any existing dial ring

    if (!audioRefs["dial_call"]) return;

    const audio = new Audio(audioRefs["dial_call"]);
    audio.loop = true; // Loop continuously
    dialRingRef.current = audio;

    audio.play().catch((err) => {
      console.warn("⚠️ [Dial] Ring play prevented:", err);
    });
  };

  const stopDialRing = () => {
    if (dialRingRef.current) {
      console.log("🔕 [Dial] Stopping dial ring loop");
      try {
        dialRingRef.current.pause();
        dialRingRef.current.currentTime = 0;
      } catch (e) {}
      dialRingRef.current = null;
    }
  };

  const handlePttState = (state, meta = {}) => {
    console.log("📡 [WebRTC] State change:", state, meta);

    if (disconnectStateTimeoutRef.current) {
      clearTimeout(disconnectStateTimeoutRef.current);
      disconnectStateTimeoutRef.current = null;
    }

    if (state === "connecting") {
      console.log("🔄 [WebRTC] Establishing connection...");
      setConnectionState("connecting");
    } else if (state === "connected" || state === "publishing") {
      console.log("✅ [WebRTC] Connection established successfully");
      clearCallTimeout();
      stopDialRing();
      setConnectionState("connected");

      if (
        (callType.isP2P || callType.isAmbient) &&
        !talkStartRef.current &&
        callStateRef.current.isIncoming
      ) {
        startTimer();
      }
    } else if (state === "closed") {
      console.log("[WebRTC] Connection closed");
      if (!callStateRef.current.inCall) {
        disconnectStateTimeoutRef.current = setTimeout(() => {
          if (!callStateRef.current.inCall) {
            setConnectionState("disconnected");
          }
          disconnectStateTimeoutRef.current = null;
        }, 120);
      } else {
        setConnectionState("disconnected");
      }
    } 
    else if (
      state === "subtrackRemoved" && !activeCalls && callType.isAmbient != true  &&
      (callTypeRef.current.isP2P === true || callTypeRef.current.isSOS === true )
    ) {
      const removedTrackId = meta?.trackId;
      console.log("[WebRTC] Subtrack removed event received, trackId:", removedTrackId, "current publishedStreamId:", callStateRef.current.remoteUserStreamId);
      if(removedTrackId === callStateRef.current.remoteUserStreamId) {
      console.log("[WebRTC] Subtrack removed in P2P/SOS call, trackId:", removedTrackId);
      disconnectCall();
      }
    } else if (
      state === "autoDisconnect" && !activeCalls && callType.isAmbient != true &&
      (callTypeRef.current.isP2P === true || callTypeRef.current.isSOS === true)
    ) {
       const removedTrackId = meta?.trackId;
       if (removedTrackId === callStateRef.current.remoteUserStreamId) {
         console.log(
           "[WebRTC] Auto-disconnect triggered due to single user timeout, disconnecting and sending disconnected event...",
         );
         disconnectCall();
       }
    }
  };
 


  const startTimer = () => {
    if (talkStartRef.current) return;

    talkStartRef.current = Date.now();
    timerThreadRef.current = setInterval(() => {
      if (!talkStartRef.current) return;

      const elapsed = Date.now() - talkStartRef.current;
      const hours = Math.floor(elapsed / 3600000);
      const minutes = Math.floor((elapsed % 3600000) / 60000);
      const seconds = Math.floor((elapsed % 60000) / 1000);

      setCallTimer(
        `${hours.toString().padStart(2, "0")}:${minutes
          .toString()
          .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`,
      );
    }, 1000);
  };

  const stopTimer = () => {
    if (timerThreadRef.current) {
      clearInterval(timerThreadRef.current);
      timerThreadRef.current = null;
    }
    talkStartRef.current = null;
    setCallTimer("00:00:00");
  };

  useEffect(() => {
    const handleAmbient = async () => {
      if (!ambientMode) {
        // User cancelled before the Ambient Connecting event arrived
        if (callStateRef.current.callKind === "Ambient" && !callStateRef.current.roomId) {
          console.log("📱 [Ambient] Cancelled before connection — stopping dial ring");
          stopDialRing();
          clearCallTimeout();
          callStateRef.current.callKind = "";
          setCallType({
            isP2P: false,
            isAmbient: false,
            isSOS: false,
            isPTTDisabled: false,
            isVideoCall: false,
          });
        }
        return;
      }

      console.log(
        "📱 [Ambient] Ambient mode activated, waiting for connection...",
      );
      await clearRunningCall();
      playDialRing();
      callStateRef.current.callKind = "Ambient";
      setCallType({
        isP2P: true,
        isAmbient: true,
        isSOS: false,
        isPTTDisabled: false,
        isVideoCall: false,
      });
      startCallTimeout(true);
    };

    handleAmbient();
  }, [ambientMode]);

  useEffect(() => {
    const handleDispatcherEvent = async () => {
      if (!dispatcherEvent || !shouldHandleDispatcherEvent(dispatcherEvent)) {
        return;
      }

      const { messageType, message, messageSentBy, activeGroup, floorWith } =
        dispatcherEvent;

      if (messageType === "broadcast-text-message" && message) {
        if (message.includes("messageType=call")) {
          await handleCallSignaling(message, messageSentBy);
        } else if (
          message.includes("messageType=PlayPublishVideo") &&
          activeGroup === callStateRef.current.roomId
        ) {
          console.log("📹 [Video] PlayPublishVideo event received");
          handleVideoPublish(message, activeGroup);
        } else if (
          message.includes("messageType=StopPublishVideo") &&
          activeGroup === callStateRef.current.roomId
        ) {
          console.log("📹 [Video] StopPublishVideo event received");
          handleVideoStop(message, activeGroup);
        }
      } else if (activeGroup === callStateRef.current.roomId) {
        handleFloorControl(messageType, floorWith);
      }
    };

    handleDispatcherEvent();
  }, [dispatcherEvent]);


  const handleCallSignaling = async (message, sender) => {
    const callInfo = parseCallMessage(message);
    if (!callInfo) return;

    const {
      callType: type,
      callState,
      streamType,
      streamId,
      roomId,
      requestId,
    } = callInfo;

    if (!callState || !type) {
      return;
    }

    const normalizedRoomId = roomId || streamId || requestId || sender;
    const isIncomingRequest = /(Requesting|Connecting)/i.test(callState) && !/streamAck/i.test(callState);

    if (isIncomingRequest) {
      if (type === "SOS") {
        // setDragAndDropEvent(dispatch, {
        //   content: message,
        //   roomId: normalizedRoomId,
        //   callType: type,
        //   streamId: streamId,
        //   streamType: streamType,
        //   requestId,
        // });
        await handleIncomingSOS(
          sender,
          streamId,
          normalizedRoomId,
          streamType === "video",
          requestId,
        );
      } else if (type === "Private") {
        await handleIncomingCall(
          sender,
          streamId,
          normalizedRoomId,
          streamType === "video",
          requestId,
        );
      } else if (type === "Ambient") {
        const isAmbientVideo = streamType === "video";

        if (ambientMode) {
          console.log(
            "📱 [Ambient] Connecting event received, stopping dial ring",
          );
          clearCallTimeout();
          stopDialRing();
          await handleIncomingAmbient(
            sender,
            streamId,
            normalizedRoomId,
            {
              isVideo: isAmbientVideo,
            },
            requestId,
          );
        } else {
          await handleIncomingAmbient(
            sender,
            streamId,
            normalizedRoomId,
            {
              queueOnly: true,
              isVideo: isAmbientVideo,
            },
            requestId,
          );
        }
      }
    } else if (/streamAck/i.test(callState)) {
      console.log(
        "✅ [ACK] streamAck received, clearing timeout and stopping dial ring",
      );
      clearCallTimeout();
      stopDialRing();
      if (callStateRef.current.inCall && !talkStartRef.current) {
        startTimer();
      }
      callStateRef.current.isIncoming = false;
    } else if (/(Disconnected|Rejected)/i.test(callState)) {
      console.log("Call Disconnected event : ", sender , "active call user :", callStateRef.current.streamId);
     
    const normalizedRoomId = requestId || roomId;
    const hasMessageRoom = Boolean(normalizedRoomId);
    const hasMessageStream = Boolean(streamId);
    const hasMessageSender = Boolean(sender);
    const hasRequestId = Boolean(requestId);
       setCallList((prev) =>
        prev.filter((c) => {
        const roomMatch = hasMessageRoom && c.roomId === normalizedRoomId;
        const streamMatch =
          hasMessageStream &&
          (c.streamId === streamId || c.roomId === streamId);
        const senderMatch = hasMessageSender && c.sender === sender;
        const requestMatch = hasRequestId && c.requestId === requestId;
        return !(roomMatch || streamMatch || senderMatch || requestMatch);
      }),
     );

      if(sender === callStateRef.current.streamId) {
      console.log("Match stream id now call disconnected start")
      handleCallDisconnect(roomId, streamId, sender, requestId);
      }
    }
  };

  const parseCallMessage = (message) => {
    const callTypeMatch = message.match(/callType\s*[:=]\s*([\w-]+)/i);
    const stateMatch = message.match(/callState\s*[:=]\s*([\w-]+)/i);
    const streamTypeMatch = message.match(/type\s*[:=]\s*([\w-]+)/i);
    const streamIdMatch = message.match(/streamId\s*[:=]\s*([\w-]+)/i);
    const roomIdMatch = message.match(/roomId\s*[:=]\s*([\w-]+)/i);
    const requestIdMatch = message.match(/requestId\s*[:=]\s*([\w-]+)/i);
    const calledToMatch = message.match(/calledTo\s*[:=]\s*([\w-]+)/i);

    return {
      callType: callTypeMatch?.[1],
      callState: stateMatch?.[1],
      streamType: streamTypeMatch?.[1]?.toLowerCase(),
      streamId: streamIdMatch?.[1],
      roomId: roomIdMatch?.[1],
      requestId: requestIdMatch?.[1],
      calledTo: calledToMatch?.[1],
    };
  };

  const queueIncomingCall = ({
    roomId,
    streamId,
    sender,
    type,
    isSOS,
    isVideo,
    requestId,
  }) => {
    const queued = addToCallQueue(
      roomId,
      streamId,
      sender,
      type,
      isSOS,
      isVideo,
      requestId,
    );

    if (!queued) {
      return;
    }

    const hasActiveCall =
      Boolean(callStateRef.current.roomId) ||
      callStateRef.current.inCall ||
      callList.length > 0;

    if (hasActiveCall) {
      playSound("beep");
      return;
    }

    if (isSOS || type === "Ambient") {
      playSound("emergency");
      // Pre-warm local media for SOS so it's ready when auto-answer fires
      if (isSOS) {
        prewarmLocalMedia(isVideo);
      }
      return;
    }

    // Pre-warm local media so it's ready when auto-answer fires
    if (type !== "Ambient") {
      prewarmLocalMedia(isVideo);
    }

    playSound("incomingCall");
  };

  const handleIncomingSOS = async (
    sender,
    streamId,
    roomId,
    isVideo,
    requestId,
  ) => {
    queueIncomingCall({
      roomId,
      streamId,
      sender,
      type: "SOS",
      isSOS: true,
      isVideo,
      requestId,
    });

    const hasActiveCall =
      Boolean(callStateRef.current.roomId) || callStateRef.current.inCall;
    if (!hasActiveCall) {
      const normalizedSender = sender || streamId || requestId || "Unknown";
      const normalizedRoomId =
        requestId || roomId || streamId || `SOS-${normalizedSender}`;
      dialWaitingCall({
        roomId: normalizedRoomId,
        streamId,
        sender: normalizedSender,
        requestId: requestId || "",
        type: "SOS",
        isSOS: true,
        isVideo,
        isAudio: !isVideo,
        queuedAt: Date.now(),
      });
    }
  };

  const handleIncomingCall = async (
    sender,
    streamId,
    roomId,
    isVideo,
    requestId,
  ) => {
    queueIncomingCall({
      roomId,
      streamId,
      sender,
      type: "Private",
      isSOS: false,
      isVideo,
      requestId,
    });
  };

  const handleIncomingAmbient = async (
    sender,
    streamId,
    roomId,
    options = {},
    requestId = "",
  ) => {
    const { queueOnly = false, isVideo = false } = options;
    const targetRoom = roomId || streamId;

    if (queueOnly) {
      queueIncomingCall({
        roomId: targetRoom,
        streamId,
        sender,
        type: "Ambient",
        isSOS: false,
        isVideo,
      });
      return;
    }

    if (callStateRef.current.callKind !== "Ambient") {
      await clearRunningCall();
    } else {
      // Already set up — just stop the dial ring and timeout
      clearCallTimeout();
      stopDialRing();
    }

    console.log("📱 [Ambient] Incoming ambient call from:", sender);
    stopAmbientRing();
    callStateRef.current.inCall = true;
    callStateRef.current.isIncoming = true;
    callStateRef.current.streamId = sender || streamId || targetRoom;
    callStateRef.current.roomId = targetRoom;
    callStateRef.current.requestId = requestId || "";
    callStateRef.current.callKind = "Ambient";
    setCallType({
      isP2P: true,
      isAmbient: true,
      isSOS: false,
      isPTTDisabled: false,
      isVideoCall: isVideo,
    });
    // No timeout needed - ambient connecting event was already received
    startPlayAmbient(targetRoom);
    setAmbientMode(dispatch, false);
  };

  const addToCallQueue = (
    roomId,
    streamId,
    sender,
    type,
    isSOS,
    isVideo,
    requestId,
  ) => {
    const normalizedSender = sender || streamId || requestId || "Unknown";
    const normalizedRoomId = requestId || roomId || streamId || `${type}-${normalizedSender}`;
    const hasDuplicate = callList.some((c) => {
      if (requestId && c.requestId) {
        return c.requestId === requestId;
      }
      return (
        c.roomId === normalizedRoomId ||
        (streamId &&
          c.streamId === streamId &&
          c.sender === normalizedSender &&
          c.type === type)
      );
    });

    if (hasDuplicate) {
      return false;
    }

    setCallList((prev) => [
      ...prev,
      {
        roomId: normalizedRoomId,
        streamId,
        sender: normalizedSender,
        requestId,
        type,
        isSOS,
        isAmbient: type === "Ambient",
        isVideo,
        isAudio: !isVideo,
        queuedAt: Date.now(),
      },
    ]);

    return true;
  };

  const handleCallDisconnect = (roomId, streamId, sender, requestId) => {
    const normalizedRoomId = requestId || roomId;
    const hasMessageRoom = Boolean(normalizedRoomId);
    const hasMessageStream = Boolean(streamId);
    const hasMessageSender = Boolean(sender);
    const hasRequestId = Boolean(requestId);

    const isCurrentCall = hasRequestId
      ? requestId === callStateRef.current.requestId
      : (hasMessageRoom && normalizedRoomId === callStateRef.current.roomId) ||
        (hasMessageStream &&
          (streamId === callStateRef.current.roomId ||
            streamId === callStateRef.current.streamId)) ||
        (hasMessageSender && sender === callStateRef.current.streamId);

    setCallList((prev) =>
      prev.filter((c) => {
        const roomMatch = hasMessageRoom && c.roomId === normalizedRoomId;
        const streamMatch =
          hasMessageStream &&
          (c.streamId === streamId || c.roomId === streamId);
        const senderMatch = hasMessageSender && c.sender === sender;
        const requestMatch = hasRequestId && c.requestId === requestId;
        return !(roomMatch || streamMatch || senderMatch || requestMatch);
      }),
    );

    if (isCurrentCall) {
      if (callStateRef.current.isDisconnecting) {
        console.log(
          "⚠️ [handleCallDisconnect] Duplicate disconnect event received while already disconnecting",
        );
        return;
      }

      // Mark disconnecting to prevent re-entry from a duplicate remote event
      callStateRef.current.isDisconnecting = true;

      // Send our own Disconnected acknowledgment BEFORE cleanup so the server
      // sees: Disconnected(Call1) → Accepted(Call2), not just Accepted(Call2).
      // callStateRef still has the correct roomId/streamId at this point.
      const disconnectTarget = callStateRef.current.streamId;
      const disconnectKind = callStateRef.current.callKind || (callType.isSOS ? "SOS" : "Private");
      if (disconnectTarget && callStateRef.current.roomId && !callStateRef.current.isBargeIn) {
        console.log("📤 [handleCallDisconnect] Sending disconnect acknowledgment to:", disconnectTarget);
        sendCallLeave(disconnectTarget, disconnectKind, callType.isVideoCall);
      }

      if (callType.isSOS) {
        setDragAndDropEvent(dispatch, "");
      }
      stopAmbientRing();
      setCallType((prev) => ({ ...prev, isPTTDisabled: false }));
      stopTimer();
      stopPreview();
      stopLocalMedia();
      cleanupP2P();
      // Toggle callEnded so auto-join re-runs consistently (same as disconnectCall path)
      setCallEnded((prev) => !prev);
    }
  };

  const handleVideoPublish = (message, activeGroup) => {
    const contentRoomMatch = message.match(/content\s*[:=]\s*play_([\w-]+)/i);
    const roomIdMatch = message.match(/roomId\s*[:=]\s*([\w-]+)/i);
    const targetRoomId =
      contentRoomMatch?.[1] || roomIdMatch?.[1] || activeGroup;

    if (!targetRoomId || targetRoomId !== callStateRef.current.roomId) return;

    const streamIdMatch = message.match(/content\s*[:=]\s*([\w-]+)/i);
    const streamId = streamIdMatch?.[1];

    if (streamId) {
      console.log("📹 [Video] Starting video playback for stream:", streamId);
      pttRef.current?._createPlayAdaptor(streamId);
    }
  };

  const handleVideoStop = (message, activeGroup) => {
    const contentRoomMatch = message.match(/content\s*[:=]\s*play_([\w-]+)/i);
    const roomIdMatch = message.match(/roomId\s*[:=]\s*([\w-]+)/i);
    const targetRoomId =
      contentRoomMatch?.[1] || roomIdMatch?.[1] || activeGroup;

    if (!targetRoomId || targetRoomId !== callStateRef.current.roomId) return;

    const streamIdMatch = message.match(/content\s*[:=]\s*([\w-]+)/i);
    const streamId = streamIdMatch?.[1];

    if (streamId) {
      console.log("📹 [Video] Stopping video playback for stream:", streamId);
      pttRef.current?._stopPlayStream(streamId);
    }
  };

  const handleFloorControl = (messageType, floorWith) => {
      // 🔹 Barge-in calls are listen-only, ignore floor control
      if (callStateRef.current.isBargeIn) {
        console.log("⏭️ [BargeIn] Ignoring floor control during listen-only barge-in");
        return;
      }

    const currentUser = AuthenticationService.getUserName();
    const floorUser = floorWith?.split("@")[0] || "Unknown";

    if (messageType === "floor-granted") {
      playSound("floor_received");
      // Enable audio tracks when floor is granted
      if (localStreamRef.current) {
        localStreamRef.current.getAudioTracks().forEach((track) => {
          track.enabled = true;
          console.log("🎤 [PTT] Audio enabled - floor granted");
        });
      }
      pttRef.current?.press?.();
      setIsMuted(false);
      setPttStatus("PTT Granted - You");
    } else if (messageType === "floor-taken") {
      if (lastPlayedUserRef.current !== floorUser) {
        playSound("floor_taken");
        lastPlayedUserRef.current = floorUser;
      }
      setPttStatus(
        floorUser === currentUser
          ? "PTT Granted - You"
          : `PTT Granted - ${floorUser}`,
      );
    } else if (
      messageType === "floor-release-ack" ||
      messageType === "floor-idle"
    ) {
      playSound("floor_release");
      // Disable audio tracks when floor is released
      if (localStreamRef.current) {
        localStreamRef.current.getAudioTracks().forEach((track) => {
          track.enabled = false;
          console.log("🔇 [PTT] Audio disabled - floor released");
        });
      }
      pttRef.current?.release?.();
      setIsMuted(true);
      setPttStatus("PTT Available");
      lastPlayedUserRef.current = null;
    }
  };

  const startPlayAmbient = async (streamId) => {
    if (pttRef.current) {
      try {
        await pttRef.current.close();
      } catch (e) {
        console.warn("Previous PTT cleanup error:", e);
      }
      pttRef.current = null;
    }

    try {
      const playStreamId = `ambient_${streamId}_${Date.now()}`;
      const playRoomId = `room_${Date.now()}`;
      callStateRef.current.publishedStreamId = playStreamId;

      const ptt = new V3MediaPTT({
        websocketUrl: serverUrl,
        streamId: playStreamId,
        roomId: playRoomId,
        onRemoteTrack: handleRemoteTrack,
        onInitialized: async () => {
          console.log(
            "✅ Ambient PTT initialized, starting playback for:",
            streamId,
          );
          stopAmbientRing(); // Stop the looping ring when stream is established
          setConnectionState("connected");
          startTimer();
          try {
            if (ptt._createPlayAdaptor) {
              await ptt._createPlayAdaptor(streamId);
              console.log("✅ Ambient playback stream created for:", streamId);
            } else {
              console.warn(
                "⚠️ _createPlayAdaptor not available, attempting direct play",
              );
              ptt.play?.(streamId);
            }
          } catch (playError) {
            console.error("❌ Play adaptor creation error:", playError);
            throw playError;
          }
        },
        onStateChange: handlePttState,
        onError: (e) => {
          console.error("❌ Ambient play error:", e);
          cleanupP2P();
        },
      });

      pttRef.current = ptt;
      await ptt.init();
    } catch (e) {
      console.error("❌ Ambient init failed:", e);
    }
  };

  const startPlayBargeIn = async (streamId) => {
    if (pttRef.current) {
      try {
        await pttRef.current.close();
      } catch (e) {
        console.warn("Previous BargeIn PTT cleanup error:", e);
      }
      pttRef.current = null;
    }

    try {
      const playStreamId = `bargein_${streamId}_${Date.now()}`;
      const playRoomId = `room_${Date.now()}`;
      callStateRef.current.publishedStreamId = playStreamId;

      const ptt = new V3MediaPTT({
        websocketUrl: serverUrl,
        streamId: playStreamId,
        roomId: playRoomId,
        onRemoteTrack: handleRemoteTrack,
        onInitialized: async () => {
          console.log("✅ [BargeIn] PTT initialized, starting listen-only playback for:", streamId);
          setConnectionState("connected");
          startTimer();
          try {
            if (ptt._createPlayAdaptor) {
              await ptt._createPlayAdaptor(streamId);
              console.log("✅ [BargeIn] Playback stream created for:", streamId);
            } else {
              ptt.play?.(streamId);
            }
          } catch (playError) {
            console.error("❌ [BargeIn] Play adaptor creation error:", playError);
            throw playError;
          }
        },
        onStateChange: handlePttState,
        onError: (e) => {
          console.error("❌ [BargeIn] Play error:", e);
          cleanupP2P();
        },
      });

      pttRef.current = ptt;
      await ptt.init();
    } catch (e) {
      console.error("❌ [BargeIn] init failed:", e);
      throw e;
    }
  };

  const prewarmLocalMedia = async (withVideo = false) => {
    if (localStreamRef.current) {
      const activeTracks = localStreamRef.current
        .getTracks()
        .filter((t) => t.readyState === "live");
      const hasVideo = activeTracks.some((t) => t.kind === "video");
      const hasAudio = activeTracks.some((t) => t.kind === "audio");
      if (withVideo ? hasVideo : hasAudio) {
        console.log(
          "🎤 [PreWarm] Already have live media tracks, skipping pre-warm",
        );
        if (withVideo && hasVideo) {
          attachLocalPreview();
        }
        return;
      }
    }
    try {
      console.log(
        "🎤 [PreWarm] Pre-warming local media for incoming call...",
      );
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
        },
        video: withVideo
          ? {
              width: { ideal: 640, max: 960 },
              height: { ideal: 360, max: 540 },
              frameRate: { ideal: 24, max: 30 },
              facingMode: "user",
            }
          : false,
      });
      localStreamRef.current = stream;
      stream.getAudioTracks().forEach((track) => {
        track.enabled = true;
      });
      if (withVideo) {
        stream.getVideoTracks().forEach((track) => {
          track.enabled = true;
        });
        setIsVideoEnabled(true);
        attachLocalPreview();
      }
      console.log("✅ [PreWarm] Local media pre-warmed and ready");
    } catch (e) {
      console.warn(
        "⚠️ [PreWarm] Media pre-warm failed (will retry on answer):",
        e,
      );
    }
  };

  const startLocalMediaForP2P = async (withVideo) => {
    try {
      // Reuse pre-warmed audio stream if available and no video needed
      if (!withVideo && localStreamRef.current) {
        const liveTracks = localStreamRef.current.getTracks().filter((t) => t.readyState === "live");
        if (liveTracks.length > 0) {
          console.log("🎤 [Media] Reusing pre-warmed audio stream — skipping getUserMedia");
          setIsVideoEnabled(false);
          clearLocalPreview();
          return localStreamRef.current;
        }
      }

      const constraints = withVideo
        ? {
            video: {
              width: { ideal: 960, max: 1280 },
              height: { ideal: 540, max: 720 },
              frameRate: { ideal: 24, max: 30 },
              facingMode: "user",
            },
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
              channelCount: 1,
            },
          }
        : {
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
              channelCount: 1,
            },
            video: false,
          };

      console.log(
        "🎤 [Media] Requesting user media:",
        withVideo ? "Audio+Video" : "Audio only",
      );
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      localStreamRef.current = stream;

      // Start with audio disabled (muted by default on call join)
      stream.getAudioTracks().forEach((track) => {
        track.enabled = true;
        console.log("🔇 [Audio] Audio track added (muted by default)");
      });

      if (withVideo) {
        stream.getVideoTracks().forEach((track) => {
          track.enabled = true;
          console.log("📹 [Video] Video track added (enabled)");
        });
        setIsVideoEnabled(true);
        attachLocalPreview();
      } else {
        setIsVideoEnabled(false);
        clearLocalPreview();
      }

      console.log(
        "✅ [Media] Local media acquired:",
        withVideo ? "Audio+Video" : "Audio",
        "- Audio muted by default",
      );
      return stream;
    } catch (e) {
      console.error("❌ getUserMedia error:", e);
      setNotify({
        isOpen: true,
        type: "error",
        pagename: "Phone",
        status: "Microphone/Camera access denied",
      });
      throw e;
    }
  };

  const sendCallAck = (
    targetUser,
    callKind = "Private",
    isVideo = callType.isVideoCall,
    requestId = null,
    roomId = callStateRef.current.roomId,
    publishStreamId = callStateRef.current.remoteUserStreamId || callStateRef.current.publishedStreamId,
  ) => {
    if (!targetUser || callStateRef.current.isStreamAckSent) return;

    callStateRef.current.isStreamAckSent = true;
    const resolvedType = isVideo ? "video" : "audio";
    const normalizedCallType = callKind === "SOS" ? "SOS" : callKind;
    const callState = callKind === "SOS" ? "Accepted" : "streamAck";

    const content = requestId
      ? `requestId: ${requestId}; type: ${resolvedType}; callType: ${normalizedCallType}; callState: ${callState}; roomId: ${roomId}; streamId: ${publishStreamId}; calledTo: ${targetUser}`
      : `roomId: ${roomId}; type: ${resolvedType.toLowerCase()}; callType: ${normalizedCallType}; callState: ${callState}; calledTo: ${targetUser}`;

    const payload = {
      kind: "D",
      sender: AuthenticationService.getExtensionNumber(),
      target: targetUser,
      content,
      messageType: "call",
    };

    postChatMessageSafe(payload);
    setAmbientMode(dispatch, false);
    console.log("✅ Call ACK sent to:", targetUser, { requestId, roomId, publishStreamId });
  };

  const sendPrivateCallJoin = (targetUser, isVideo) => {
    const payload = {
      kind: "D",
      sender: AuthenticationService.getExtensionNumber(),
      target: targetUser,
      content: `roomId: ${callStateRef.current.roomId}; type: ${
        isVideo ? "video" : "audio"
      }; callType: Private; callState: Connecting; calledTo: ${targetUser}`,
      messageType: "call",
    };

    postChatMessageSafe(payload);
    console.log("✅ Call join sent to:", targetUser);
  };

  const sendCallLeave = (
    targetUser,
    callKind = callStateRef.current.callKind || "Private",
    isVideo = callType.isVideoCall,
  ) => {
    const payload = {
      kind: "D",
      sender: AuthenticationService.getExtensionNumber(),
      target: targetUser,
      content: `roomId: ${callStateRef.current.roomId}; type: ${
        isVideo ? "video" : "audio"
      }; callType: ${callKind}; callState: Disconnected; calledTo: ${targetUser}`,
      messageType: "call",
    };
    try {
      postChatMessageSafe(payload);
    } catch (e) {
      console.log("Failed to send call leave message:", e);
    }

    console.log("✅ Call leave sent to:", targetUser);
  };

  const dial = async (isVideo = false, room = null) => {
    closePTT();
    setCallType((prev) => ({
      ...prev,
      isPTTDisabled: true,
      isVideoCall: isVideo,
    }));
    callStateRef.current.isStreamAckSent = false;
    callStateRef.current.callKind =
      callStateRef.current.callKind || (callType.isSOS ? "SOS" : "Private");
    setIsMuted(false); // Reset mute state to true on new call

    try {
      await startLocalMediaForP2P(isVideo);

      callStateRef.current.roomId = room;
      const streamId = `stream${Date.now()}`;
      callStateRef.current.publishedStreamId = streamId;

      console.log(
        "📞 [Call] Initializing P2P call - Room:",
        callStateRef.current.roomId,
        "Stream:",
        streamId,
      );
      const ptt = new V3MediaPTT({
        websocketUrl: serverUrl,
        streamId,
        roomId: callStateRef.current.roomId,
        onRemoteTrack: handleRemoteTrack,
        onInitialized: () => {
          console.log("✅ [Call] P2P call initialized successfully");
          setConnectionState("connected");

          if (callStateRef.current.isIncoming) {
            if (!activeCalls) {
              sendCallAck(
                callStateRef.current.streamId,
                callStateRef.current.callKind || "Private",
                isVideo,
              );
            }
            clearCallTimeout();
            stopDialRing();
            startTimer();
            console.log("⏱️ [Timer] Incoming call connected, timer started");
          } else {
            console.log(
              "⏱️ [Timer] Waiting for streamAck from remote party...",
            );
          }
        },
        onStateChange: handlePttState,
        onError: (e) => {
          const definition = typeof e === "object" ? e?.definition : e;
          if (definition === "noPacketReceivedForTimeoutDuration") {
            // Transient server-side packet timeout — V3MediaPTT will attempt
            // reconnect automatically. Do NOT cleanupP2P here, or it sets
            // ptt.closed=true and the reconnect is immediately aborted.
            console.warn("⚠️ [Call] Packet timeout, reconnect scheduled:", e);
            return;
          }
          console.error("❌ [Call] P2P call error:", e);
          setNotify({
            isOpen: true,
            type: "error",
            pagename: "Conference",
            status: "Call connection failed",
          });
          cleanupP2P();
        },
      });

      pttRef.current = ptt;
      await ptt.playPublish(
        localStreamRef.current,
        callStateRef.current.roomId,
        isVideo,
      );
      if (!callStateRef.current.isIncoming) {
        startCallTimeout(false);
      }
    } catch (e) {
      console.error("❌ Dial error:", e);
      clearCallTimeout();
      disconnectCall();
      cleanupP2P();
    }
  };

  const dialWaitingCall = async (call) => {
    console.log("📞 [WaitingCall] Starting to join waiting call:", call.sender);
    if (isJoiningRef.current) {
      console.warn("⚠️ [WaitingCall] Join already in progress, skipping duplicate");
      return;
    }
    isJoiningRef.current = true;

    if (call.type === "Ambient") {
      const isVideoAmbient = Boolean(call.isVideo);

      await clearRunningCall();
      stopTimer();
      setCallList((prev) => prev.filter((c) => c.roomId !== call.roomId));
      setIsCallListOpen(false);

      callStateRef.current.inCall = true;
      callStateRef.current.isIncoming = true;
      callStateRef.current.isDialing = false;
      callStateRef.current.isStreamAckSent = false;
      callStateRef.current.streamId =
        call.sender || call.streamId || call.requestId || call.roomId;
      callStateRef.current.roomId = call.streamId || call.roomId;
      callStateRef.current.callKind = "Ambient";

      setCallType({
        isP2P: true,
        isSOS: false,
        isAmbient: true,
        isPTTDisabled: false,
        isVideoCall: isVideoAmbient,
      });

      stopDialRing();
      clearCallTimeout();
      sendCallAck(call.sender, "Ambient", isVideoAmbient);
      await startPlayAmbient(call.streamId || call.roomId);
      isJoiningRef.current = false;
      return;
    }

     setDragAndDropEvent(dispatch, {
          sender: call.sender,
          roomId: call.roomId,
          callType: call.isVideo,
        });

    lastQueuedRingRoomRef.current = call.roomId;
    console.log("✅ [WaitingCall] Marked call as attempted:", call.roomId);
    const hasActiveCallToClean = Boolean(callStateRef.current.roomId) || Boolean(pttRef.current);
    if (hasActiveCallToClean) {
      await clearRunningCall();
      stopTimer();

    
      const callStillPending = callListRef.current.some((c) => c.roomId === call.roomId);
      if (!callStillPending) {
        console.warn("⚠️ [WaitingCall] Join cancelled during clearRunningCall — call already gone");
        isJoiningRef.current = false;
        return;
      }
      isJoiningRef.current = true;
    } else {
      stopTimer();
    }

    setCallList((prev) => prev.filter((c) => c.roomId !== call.roomId));
    setIsCallListOpen(false);

    callStateRef.current.inCall = true;
    callStateRef.current.streamId = call.sender || call.streamId || call.requestId || call.roomId;
    callStateRef.current.requestId = call.requestId || "";
    callStateRef.current.callKind = call.type || "Private";
    callStateRef.current.isStreamAckSent = false;
    callStateRef.current.isIncoming = true; // Mark this as incoming join

    if (call.isSOS) {
      callStateRef.current.roomId = AuthenticationService.getExtensionNumber();
    } else {
      callStateRef.current.roomId = call.roomId;
    }

    const publishStreamId = `streamRemoteUser${Date.now()}`;
    // callStateRef.current.publishedStreamId = publishStreamId;
    callStateRef.current.remoteUserStreamId = publishStreamId;
    if (call.isSOS) {
      sendCallAck(
        call.sender,
        "SOS",
        call.isVideo,
        call.requestId,
        callStateRef.current.roomId,
        publishStreamId,
      );
    } else {
      sendCallAck(call.sender, call.type || "Private", call.isVideo);
    }

    setCallType({
      isP2P: true,
      isSOS: call.isSOS,
      isAmbient: false,
      isPTTDisabled: true,
      isVideoCall: call.isVideo,
    });

    closePTT();
    setIsMuted(false); // Start muted on waiting call join
    const isVideo = call.isVideo;
    let waitingCallFailed = false;

    const failWaitingCall = (reason, error) => {
      if (waitingCallFailed) return;
      waitingCallFailed = true;
      console.error("❌ [WaitingCall] Failure handler:", reason, error);
      isJoiningRef.current = false;
      clearCallTimeout();
      stopDialRing();
      disconnectWaitingCall(call);
      cleanupP2P();
      setNotify({
        isOpen: true,
        type: "error",
        pagename: "Conference",
        status: "Failed to establish connection",
      });
    };

    try {
      console.log(
        "📞 [WaitingCall] Requesting media for:",
        isVideo ? "Video" : "Audio",
      );
      await startLocalMediaForP2P(isVideo);

      if (!isJoiningRef.current) {
        console.warn("⚠️ [WaitingCall] Join cancelled after getUserMedia — call already gone");
        stopLocalMedia();
        return;
      }

      const streamId = `streamLocalUser${Date.now()}`;
      callStateRef.current.publishedStreamId = streamId;
      console.log("📞 [WaitingCall] Initializing V3MediaPTT connection");

       if (call.isSOS) {
      console.log("🚨 [SOS] Joining SOS call:", {
        room: callStateRef.current.roomId,
        dispatcher: streamId,
        remote: { name: call.sender, streamId: call.streamId },
      });
    }

      const ptt = new V3MediaPTT({
        websocketUrl: serverUrl,
        streamId,
        roomId: callStateRef.current.roomId,
        onRemoteTrack: handleRemoteTrack,
        onInitialized: () => {
          console.log(
            "✅ [WaitingCall] V3MediaPTT initialized, connection ready",
          );
          setConnectionState("connected");
          clearCallTimeout();
          stopDialRing();
          startTimer();
          isJoiningRef.current = false;
          console.log("✅ [WaitingCall] Call established and timer started");
        },
        onStateChange: handlePttState,
        onError: (e) => {
          failWaitingCall("Connection error", e);
        },
      });

      pttRef.current = ptt;
      console.log("📞 [WaitingCall] Publishing media stream...");
      await ptt.playPublish(
        localStreamRef.current,
        callStateRef.current.roomId,
        isVideo,
      );

      // If cleanup fired while we were publishing, abort.
      if (!isJoiningRef.current) {
        console.warn("⚠️ [WaitingCall] Join cancelled after publish — call already gone");
        return;
      }

      console.log("✅ [WaitingCall] Media published successfully");
    } catch (e) {
      console.error("❌ [WaitingCall] Error joining call:", e);
      if (!waitingCallFailed) {
        isJoiningRef.current = false;
        clearCallTimeout();
        // Only notify and reject if we're still the active call (not already disconnected).
        if (callStateRef.current.roomId === call.roomId) {
          setNotify({
            isOpen: true,
            type: "error",
            pagename: "Conference",
            status: "Failed to join call",
          });
          disconnectWaitingCall(call);
        }
        cleanupP2P();
      }
    }
  };

  const clearRunningCall = async () => {
    try {
      console.log("🧹 Clearing running call...", {
        roomId: callStateRef.current.roomId,
        isP2P: callType.isP2P,
        isSOS: callType.isSOS,
        isAmbient: callType.isAmbient,
      });

      if (callStateRef.current.isClearing) {
        console.log("⚠️ Already clearing, skipping...");
        return;
      }
      callStateRef.current.isClearing = true;
      const currentStreamId = callStateRef.current.streamId;
      const currentRoomId = callStateRef.current.roomId;
      stopAmbientRing(); // Stop ambient ring if playing
      stopTimer();
      stopPreview();
      stopLocalMedia();

      if (callType.isP2P || callType.isSOS) {
        disconnectCall(currentStreamId, currentRoomId);
      } else if (callType.isAmbient) {
        disconnectAmbientCall();
      }

      if (pttRef.current) {
        try {
          await leaveConference();
          await pttRef.current?.close();
          pttRef.current = null;
        } catch (err) {
          console.warn("⚠️ PTT leave error:", err);
        }
      }

      cleanupState();

      await new Promise((resolve) => setTimeout(resolve, 20));

      console.log("✅ Call cleared successfully");
    } catch (error) {
      console.error("❌ clearRunningCall error:", error);
    } finally {
      callStateRef.current.isClearing = false;
    }
  };

  useEffect(() => {
    const handleClickToCall = async () => {
      if (!clickToCall) return;
      if (callStateRef.current.inCall) {
        console.warn("⚠️ Already in call, ignoring click-to-call");
        setNotify({
          isOpen: true,
          type: "warning",
          pagename: "Phone",
          status: "Already in a call. Please disconnect first.",
        });
        return;
      }

      if (shouldPostWebViewCallEvent(clickToCall)) {
        postWebViewCallEvent(clickToCall, false);
        return;
      }

      console.log("📞 [Dial] Click-to-call initiated to:", clickToCall);
      await clearRunningCall();
      playDialRing();

      const callUUID = (crypto.randomUUID && crypto.randomUUID()) || generateUUID();
      callStateRef.current.isDialing = true;
      callStateRef.current.inCall = true;
      callStateRef.current.isIncoming = false;
      callStateRef.current.roomId = callUUID;
      callStateRef.current.streamId = clickToCall;
      callStateRef.current.callKind = "Private";

      setCallType({
        isP2P: true,
        isSOS: false,
        isAmbient: false,
        isPTTDisabled: false,
        isVideoCall: false,
      });
      sendPrivateCallJoin(clickToCall, false);
      dial(false, callUUID); // This will start the 10-second timeout
    };

    handleClickToCall();
  }, [clickToCall]);

    useEffect(() => {
      const handleActiveCall = async () => {
        if (!activeCalls) return;
        if (callStateRef.current.inCall) {
          console.warn("⚠️ Already in call, ignoring active-call");
          setNotify({
          isOpen: true,
          type: "warning",
          pagename: "Phone",
          status: "Already in a call. Please disconnect first.",
        });
          return;
        }

        const activeCallPayload =
          typeof activeCalls === "object" && activeCalls !== null
            ? activeCalls
            : { streamID: activeCalls };

        const streamID = String(
          activeCallPayload?.streamID || activeCallPayload?.streamId || "",
        ).trim();
        const sentFrom = String(activeCallPayload?.sentFrom || "").trim();
        const sentTo = String(activeCallPayload?.sentTo || "").trim();

        const roomId = streamID || String(activeCalls).trim();

        if (!roomId) {
          console.warn("⚠️ Active call payload missing roomId", activeCallPayload);
          return;
        }

        console.log("📞 [BargeIn] Barge-in initiated:", {
          roomId,
          sentFrom,
          sentTo,
        });

        await joinBargeInCall(roomId, sentFrom, sentTo);
      };

      handleActiveCall();
    }, [activeCalls]);

  useEffect(() => {
    const handleClickToVideoCall = async () => {
      if (!clickToVideoCall) return;
      if (callStateRef.current.inCall) {
        console.warn("⚠️ Already in call, ignoring click-to-video-call");
        setNotify({
          isOpen: true,
          type: "warning",
          pagename: "Phone",
          status: "Already in a call. Please disconnect first.",
        });
        return;
      }

      if (shouldPostWebViewCallEvent(clickToVideoCall)) {
        postWebViewCallEvent(clickToVideoCall, true);
        return;
      }


      console.log(
        "📹 [Dial] Click-to-video-call initiated to:",
        clickToVideoCall,
      );
      await clearRunningCall();
      playDialRing();

      const callUUID = (crypto.randomUUID && crypto.randomUUID()) || generateUUID();
      callStateRef.current.isDialing = true;
      callStateRef.current.inCall = true;
      callStateRef.current.isIncoming = false;
      callStateRef.current.roomId = callUUID;
      callStateRef.current.streamId = clickToVideoCall;
      callStateRef.current.callKind = "Private";

      setCallType({
        isP2P: true,
        isSOS: false,
        isAmbient: false,
        isPTTDisabled: false,
        isVideoCall: true,
      });
      sendPrivateCallJoin(clickToVideoCall, true);
      dial(true, callUUID); // This will start the 10-second timeout
    };

    handleClickToVideoCall();
  }, [clickToVideoCall]);

  const dialCall = async (isVideo, overrideTarget = null) => {
    const targetValue = String(overrideTarget ?? dialValue)
      .replace(/[^\d*#]/g, "")
      .trim();

    if (!targetValue) return false;

    if (targetValue === AuthenticationService.getExtensionNumber()) {
      setNotify({
        isOpen: true,
        type: "warning",
        pagename: "Phone",
        status: "Cannot call your own extension",
      });
      return false;
    }
    if (callStateRef.current.inCall) {
      console.warn("⚠️ Already in call, cannot dial");
      setNotify({
        isOpen: true,
        type: "warning",
        pagename: "Phone",
        status: "Already in a call. Please disconnect first.",
      });
      return false;
    }

    if (shouldPostWebViewCallEvent(targetValue)) {
      postWebViewCallEvent(targetValue, isVideo);
      return;
    }


    console.log(
      `📞 [Dial] Manual ${isVideo ? "video" : "audio"} call initiated to:`,
      targetValue,
    );
    await clearRunningCall();
    playDialRing();

    const callUUID = (crypto.randomUUID && crypto.randomUUID()) || generateUUID();
    const target = targetValue;

    callStateRef.current.isDialing = true;
    callStateRef.current.inCall = true;
    callStateRef.current.isIncoming = false;
    callStateRef.current.roomId = callUUID;
    callStateRef.current.streamId = target;
    callStateRef.current.callKind = "Private";

    setCallType({
      isP2P: true,
      isSOS: false,
      isAmbient: false,
      isPTTDisabled: false,
      isVideoCall: isVideo,
    });

    if (overrideTarget === null) {
      setDialValue("");
    }

    sendPrivateCallJoin(target, isVideo);
    dial(isVideo, callUUID); // This will start the 10-second timeout
    return true;
  };

  const answerNextCall = async () => {
    const nextWaitingCall = callList[0];

    if (!nextWaitingCall || callStateRef.current.inCall) {
      return false;
    }

    await dialWaitingCall(nextWaitingCall);
    return true;
  };


  const joinBargeInCall = async (roomId, sentFrom, sentTo) => {
    try {
      console.log("📞 [BargeIn] Starting barge-in call join", {
        roomId,
        sentFrom,
        sentTo,
      });

      await clearRunningCall();

      callStateRef.current.inCall = true;
      callStateRef.current.isBargeIn = true;
      callStateRef.current.bargeInSentFrom = sentFrom;
      callStateRef.current.bargeInSentTo = sentTo;
      callStateRef.current.roomId = roomId;
      callStateRef.current.streamId = "Barge-In-Listen";
      callStateRef.current.callKind = "BargeIn";

      setCallType({
        isP2P: true,
        isSOS: false,
        isAmbient: true,
        isPTTDisabled: false,
        isVideoCall: false,
      });

      // Listen-only mode: never keep/publish any local media.
      stopLocalMedia();
      setIsMuted(true);
      setPttStatus("Listening - Listen Only");

      await startPlayBargeIn(roomId);
    } catch (e) {
      console.error("❌ [BargeIn] Error joining barge-in call:", e);
      callStateRef.current.isBargeIn = false;
      callStateRef.current.inCall = false;
      setNotify({
        isOpen: true,
        type: "error",
        pagename: "Phone",
        status: "Failed to join barge-in call",
      });
      cleanupP2P();
    }
  };

  const rejectNextCall = () => {
    const nextWaitingCall = callList[0];
    if (!nextWaitingCall) {
      return false;
    }

    disconnectWaitingCall(nextWaitingCall);
    return true;
  };

  const disconnectActiveCall = async () => {
    if (callStateRef.current.roomId && !callStateRef.current.isDisconnecting) {
      closePTT();
      stopDialRing();
      clearCallTimeout();

      if (callType.isAmbient) {
        await disconnectAmbientCall();
        return true;
      }

      if (callType.isP2P || callType.isSOS) {
        disconnectCall();
        return true;
      }

      await leaveConference();
      return true;
    }

    if (!callStateRef.current.roomId && callList.length > 0) {
      disconnectWaitingCall(callList[0]);
      return true;
    }

    return false;
  };

  useImperativeHandle(ref, () => ({
    dialAudio: (targetValue) => dialCall(false, targetValue),
    dialVideo: (targetValue) => dialCall(true, targetValue),
    answerNextCall,
    rejectNextCall,
    disconnectActiveCall,
    toggleMic: () => handlePTT(),
    toggleVideo: () => toggleVideo(),
    leaveConferenceCall: () => leaveConference(),
    disconnectAmbient: () => disconnectAmbientCall(),
    openVideoPopup: () => setIsVideoMaximized(true),
    closeVideoPopup: () => setIsVideoMaximized(false),
    joinCall: (call) => dialWaitingCall(call),
    rejectCall: (call) => disconnectWaitingCall(call),
  }));

  const disconnectCall = (targetStreamId = null, roomId = null) => {
    if (callStateRef.current.isDisconnecting) {
      console.log("⚠️ Already disconnecting, skipping...");
      return;
    }

    callStateRef.current.isDisconnecting = true;

    try {
      stopDialRing(); // Stop dial ring on disconnect
      clearCallTimeout(); // Clear timeout on disconnect
      const target = targetStreamId || callStateRef.current.streamId;
      const room = roomId || callStateRef.current.roomId;

        if (target && room && !callStateRef.current.isBargeIn && target !== "Barge-In-Listen") {
        const originalStreamId = callStateRef.current.streamId;
        const originalRoomId = callStateRef.current.roomId;
        const isActiveSOS = callType.isSOS && callStateRef.current.inCall;

        if (targetStreamId) callStateRef.current.streamId = targetStreamId;
        if (roomId) callStateRef.current.roomId = roomId;

       sendCallLeave(target, isActiveSOS ? "SOS" : callStateRef.current.callKind || "Private");
      }
      stopTimer();
      // Immediately reset reactive state so onStatusChange fires with
      // isVideoCall:false / remoteStreams:[] — this lets the Dispatcher
      // close the video tab and update the toolbar right away.
      callStateRef.current.inCall = false;
      callStateRef.current.isBargeIn = false;
      callStateRef.current.bargeInSentFrom = "";
      callStateRef.current.bargeInSentTo = "";
      callStateRef.current.callKind = "";
      setRemoteStreams([]);
      setCallType({
        isP2P: false,
        isSOS: false,
        isAmbient: false,
        isPTTDisabled: false,
        isVideoCall: false,
      });
      setIsMuted(true);
      setIsVideoEnabled(false);
      setPttStatus("PTT Available");

      // Delayed media / WS / PeerConnection cleanup — the refs still
      // hold the roomId so the WS "leave" command is sent correctly.
      setTimeout(() => {
        cleanupP2P();
        console.log(
          "🔔 [Disconnect] Cleanup complete - triggering auto-join check",
        );
        callStateRef.current.isDisconnecting = false;
        setCallEnded((prev) => !prev);
      }, 20);
    } catch (e) {
      callStateRef.current.isDisconnecting = false;
      throw e;
    }
  };

  const disconnectWaitingCall = (call) => {
    setCallList((prev) => prev.filter((c) => c.roomId !== call.roomId));

    const target = call.sender || call.streamId;
    if (!target) {
      return;
    }

    const content = call.isSOS
      ? `requestId: ${call.requestId || ""}; type: ${
          call.isVideo ? "VIDEO" : "AUDIO"
        }; callType: SOS; callState: Rejected; calledTo: ${target}`
      : `roomId: ${call.roomId}; type: ${
          call.isVideo ? "video" : "audio"
        }; callType: ${call.type}; callState: Disconnected; calledTo: ${target}`;

    const payload = {
      kind: "D",
      sender: AuthenticationService.getExtensionNumber(),
      target,
      content,
      messageType: "call",
    };

    postChatMessageSafe(payload);
    console.log("🔴 Waiting call rejected:", target, { requestId: call.requestId });
  };

  const disconnectAmbientCall = async () => {
    const target = callStateRef.current.streamId;
    if (!target) {
      setAmbientMode(dispatch, false);
      return;
    }

    console.log("🔴 Disconnecting ambient:", target);
    stopAmbientRing(); // Stop ambient ring on disconnect
    stopDialRing(); // Stop dial ring on ambient disconnect
    clearCallTimeout(); // Clear timeout on ambient disconnect
    stopTimer();

    const payload = {
      kind: "D",
      sender: AuthenticationService.getExtensionNumber(),
      target,
      content: "callType: Ambient; callState: Disconnected;",
      messageType: "call",
    };

    postChatMessageSafe(payload);
    setAmbientMode(dispatch, false);
    try {
      if (pttRef.current) {
        pttRef.current._stopPlayStream?.(target);
        await pttRef.current?.close?.();
      }
    } catch (e) {
      console.warn("⚠️ Ambient stream stop error:", e);
    }
    pttRef.current = null;

    cleanupState();
  };

  const disconnectLiveGroupCall = async () => {
    const target = callStateRef.current.roomId;
    if (!target) return;

    console.log("🔴 Disconnecting live group call:", target);

    const payload = {
      kind: "G",
      sender: AuthenticationService.getExtensionNumber(),
      target,
      content: "Disconnect",
      messageType: "liveConferenceDisconnect",
    };

    postChatMessageSafe(payload);
    try {
      if (pttRef.current) {
        pttRef.current._stopPlayStream?.(target);
        await pttRef.current?.close?.();
      }
    } catch (e) {
      console.warn("⚠️ Live group call stop error:", e);
    }
    pttRef.current = null;
    cleanupState();
  };

  const cleanupState = () => {
    console.log("🧹 Cleaning up all state...");

    stopDialRing();
    clearCallTimeout();

    // Reset auto-join tracking when state is cleaned up - ready for next join attempt
    console.log("🧹 [Cleanup] Resetting auto-join tracking for next call");
    lastQueuedRingRoomRef.current = null;
    if (autoJoinTimeoutRef.current) {
      clearTimeout(autoJoinTimeoutRef.current);
      autoJoinTimeoutRef.current = null;
    }
    if (disconnectStateTimeoutRef.current) {
      clearTimeout(disconnectStateTimeoutRef.current);
      disconnectStateTimeoutRef.current = null;
    }

    wsRef.current = null;

    if (dataChannelRef.current) {
      console.log("📡 [DataChannel] Clearing dataChannel reference:", {
        label: dataChannelRef.current.label || "N/A",
        readyState: dataChannelRef.current.readyState || "unknown",
      });
    }
    dataChannelRef.current = null;

    if (pcRef.current) {
      console.log("🔌 [PeerConnection] Clearing peer connection reference:", {
        connectionState: pcRef.current.connectionState || "unknown",
        iceConnectionState: pcRef.current.iceConnectionState || "unknown",
        signalingState: pcRef.current.signalingState || "unknown",
      });
    }
    pcRef.current = null;

    pttRef.current = null;
    localStreamRef.current = null;

    callStateRef.current = {
      roomId: "",
      streamId: "",
      publishedStreamId: "",
      requestId: "",
      callKind: "",
      isClearing: false,
      isDisconnecting: false,
      isStreamAckSent: false,
      inCall: false,
      isDialing: false,
      isIncoming: false,
      isBargeIn: false,
      bargeInSentFrom: "",
      bargeInSentTo: "",
      remoteUserStreamId: "",
    };

    setConnectionState("idle");
    setCallType({
      isP2P: false,
      isSOS: false,
      isAmbient: false,
      isPTTDisabled: false,
      isVideoCall: false,
    });
    setRemoteStreams([]);
    setIsMuted(true);
    setPttStatus("PTT Available");
    setIsVideoEnabled(false);

    setClickToVideoCall(dispatch, "");
    setClickToCall(dispatch, "");
    setActiveCalls(dispatch, "");
    setRTCEvent(dispatch, null);

    console.log(
      "✅ State cleanup complete - refs and state reset ready for next call",
    );
  };

  const cleanupP2P = () => {
    console.log("🧹 Cleaning up P2P resources...");

    // Cancel any in-progress join attempt so dialWaitingCall bails out cleanly.
    isJoiningRef.current = false;

    stopDialRing();
    clearCallTimeout();
    setDragAndDropEvent(dispatch, "");
    if (pcRef.current) {
      try {
        console.log("🔌 [PeerConnection] Closing peer connection:", {
          connectionState: pcRef.current.connectionState,
          iceConnectionState: pcRef.current.iceConnectionState,
          iceGatheringState: pcRef.current.iceGatheringState,
          signalingState: pcRef.current.signalingState,
          localDescription: pcRef.current.localDescription?.type || "none",
          remoteDescription: pcRef.current.remoteDescription?.type || "none",
        });
        pcRef.current.getSenders().forEach((sender) => sender.track?.stop());
        pcRef.current.close();
        console.log("✅ [PeerConnection] Peer connection closed");
      } catch (e) {
        console.warn("⚠️ PC cleanup error:", e);
      }
      pcRef.current = null;
    }

    stopLocalMedia();
    stopTimer();

    if (dataChannelRef.current) {
      try {
        console.log("📡 [DataChannel] Closing data channel:", {
          label: dataChannelRef.current.label,
          readyState: dataChannelRef.current.readyState,
          id: dataChannelRef.current.id,
          protocol: dataChannelRef.current.protocol,
          ordered: dataChannelRef.current.ordered,
          maxRetransmits: dataChannelRef.current.maxRetransmits,
          bufferedAmount: dataChannelRef.current.bufferedAmount,
        });
        dataChannelRef.current.close();
        console.log("✅ [DataChannel] Data channel closed");
      } catch (e) {
        console.warn("⚠️ [DataChannel] Close error:", e);
      }
      dataChannelRef.current = null;
    }

    if (wsRef.current) {
      try {
        console.log("🌐 [WebSocket] Closing WebSocket connection:", {
          url: wsRef.current.url,
          readyState: wsRef.current.readyState,
          readyStateText:
            wsRef.current.readyState === 0
              ? "CONNECTING"
              : wsRef.current.readyState === 1
                ? "OPEN"
                : wsRef.current.readyState === 2
                  ? "CLOSING"
                  : wsRef.current.readyState === 3
                    ? "CLOSED"
                    : "UNKNOWN",
          protocol: wsRef.current.protocol,
          bufferedAmount: wsRef.current.bufferedAmount,
        });
        if (wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(
            JSON.stringify({
              command: "leave",
              streamId: callStateRef.current.roomId,
            }),
          );
          console.log("📤 [WebSocket] Sent leave command");
        }
        wsRef.current.close();
        console.log("✅ [WebSocket] WebSocket closed");
      } catch (e) {
        console.warn("⚠️ WS cleanup error:", e);
      }
      wsRef.current = null;
    }

    remoteStreams.forEach((remote) => {
      try {
        remote.stream.getTracks().forEach((track) => track.stop());
      } catch (e) {}
    });
    setRemoteStreams([]);

    if (pttRef.current) {
      try {
        pttRef.current._stopPlayStream(callStateRef.current.roomId);
        pttRef.current.close();
      } catch (e) {
        console.warn("⚠️ PTT cleanup error:", e);
      }
      pttRef.current = null;
    }

    cleanupState();
    console.log("✅ P2P cleanup complete");
  };

  const leaveConference = async () => {
    console.log("🚪 Leaving conference...");

    stopDialRing(); // Stop dial ring on leave
    clearCallTimeout(); // Clear timeout on leave

    if (liveGroupCall.current) {
      disconnectLiveGroupCall();
    }

    if (callType.isAmbient) {
      stopTimer();
      try {
        if (pttRef.current) {
          await pttRef.current?.close?.();
        }
      } catch (e) {
        console.warn("⚠️ Ambient PTT close error:", e);
      }
      cleanupState();
      return;
    }

    remoteStreams.forEach((remote) => {
      try {
        remote.stream.getTracks().forEach((track) => track.stop());
      } catch (e) {}
    });

    stopPreview();
    stopTimer();

    if (pttRef.current) {
      try {
        await pttRef.current.close();
      } catch (e) {
        console.warn("⚠️ PTT close error:", e);
      }
      pttRef.current = null;
    }

    callStateRef.current.inCall = false;
    setRemoteStreams([]);
    setConnectionState("disconnected");
    cleanupState();
    closePTT();

    console.log("✅ Conference left successfully");
  };

  const closePTT = () => {
    try {
      pttRef.current?.close();
    } catch (e) {
      console.warn("⚠️ PTT close error:", e);
    }
    pttRef.current = null;

    if (callStateRef.current.roomId && !callStateRef.current.isBargeIn) {
      sendPttMessage("floor-release", callStateRef.current.roomId);
    }

    setPttStatus("PTT Available");
    setIsMuted(true);
    setConnectionState("idle");
  };

  const handlePTT = () => {
    if (callStateRef.current.isBargeIn) {
      console.log("⏭️ [BargeIn] Mic toggle blocked in listen-only mode");
      return;
    }

    if (isMuted) {
      handleMicDown();
    } else {
      handleMicUp();
    }
  };

  const handleMicDown = async () => {
    try {
      if (callStateRef.current.isBargeIn) {
        console.log("⏭️ [BargeIn] Mic down blocked in listen-only mode");
        return;
      }

      if (callType.isPTTDisabled) {
        // P2P calls - direct control
        if (localStreamRef.current) {
          localStreamRef.current
            .getAudioTracks()
            .forEach((track) => (track.enabled = true));
          setIsMuted(false);
          playSound("floor_taken");
          console.log("🎤 [P2P] Audio enabled");
        }
      } else {
        // Group calls with PTT - request floor, don't enable audio yet
        console.log("📢 [PTT] Requesting floor access...");
        sendPttMessage("floor-request", callStateRef.current.roomId);
        // Don't call press() here - let floor-granted handle it
        // Audio will be enabled when floor-granted message arrives
      }
    } catch (e) {
      console.error("⚠️ Mic down error:", e);
    }
  };

  const handleMicUp = () => {
    try {
      if (callStateRef.current.isBargeIn) {
        console.log("⏭️ [BargeIn] Mic up blocked in listen-only mode");
        return;
      }

      if (callType.isPTTDisabled) {
        // P2P calls - direct control
        if (localStreamRef.current) {
          localStreamRef.current
            .getAudioTracks()
            .forEach((track) => (track.enabled = false));
          setIsMuted(true);
          playSound("floor_release");
          console.log("🔇 [P2P] Audio disabled");
        }
      } else {
        // Group calls with PTT - release floor
        console.log("📢 [PTT] Releasing floor...");
        sendPttMessage("floor-release", callStateRef.current.roomId);
        // Don't call release() here - let floor-release-ack handle it
        // Audio will be disabled when floor-release-ack arrives
      }
    } catch (e) {
      console.error("⚠️ Mic up error:", e);
    }
  };

  const toggleVideo = async () => {
    const tracks = localStreamRef.current?.getVideoTracks?.() || [];
    const next = !isVideoEnabled;

    console.log(`🎥 [Video] Toggle video ${next ? "ON" : "OFF"}`);

    if (next && !tracks.length) {
      // Start video stream
      console.log("📹 [Video] Requesting camera access...");
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 1280, min: 640 },
            height: { ideal: 720, min: 480 },
            frameRate: { ideal: 30, max: 60 },
            facingMode: "user",
          },
          audio: false,
        });
        const [videoTrack] = stream.getVideoTracks();
        if (videoTrack && localStreamRef.current) {
          console.log("✅ [Video] Camera access granted, adding video track");
          localStreamRef.current.addTrack(videoTrack);
          await pttRef.current?.upgradeToVideo?.(new MediaStream([videoTrack]));
          console.log("✅ [Video] Video stream started and upgraded");
          setIsVideoEnabled(true);
        }
      } catch (e) {
        console.error("❌ [Video] Camera access error:", e);
        setNotify({
          isOpen: true,
          type: "error",
          pagename: "Phone",
          status: "Camera access denied",
        });
        return;
      }
    } else if (!next && tracks.length) {
      // Disable video tracks without stopping them — stopping removes RTP packets
      // entirely, which causes the media server to fire noPacketReceivedForTimeoutDuration
      // and drop the call. Disabling sends black frames and keeps the connection alive.
      console.log("⏸️ [Video] Disabling video stream...");
      tracks.forEach((track) => {
        track.enabled = false;
      });
      console.log("✅ [Video] Video stream disabled");
      setIsVideoEnabled(false);
    } else if (next && tracks.length) {
      // Resume existing tracks
      console.log("▶️ [Video] Resuming video stream...");
      tracks.forEach((track) => {
        track.enabled = true;
      });
      setIsVideoEnabled(true);
      console.log("✅ [Video] Video stream resumed");
    } else {
      // Pause tracks
      console.log("⏸️ [Video] Pausing video stream...");
      tracks.forEach((track) => {
        track.enabled = false;
      });
      setIsVideoEnabled(false);
      console.log("✅ [Video] Video stream paused");
    }
  };

  const appendDial = (char) => setDialValue((v) => (v || "") + char);
  const clearDial = () => setDialValue("");

  const handleAnnouncementDialogClose = () => {
    setAnnouncement({ isActive: false, department: "" });
    setRTCEvent(dispatch, null);
  };

  const announcementRecorder = (
    <AudioRecorder
      uploadUrl="file/upload-recordings"
      method="POST"
      headers={{
        Authorization: `Bearer ${AuthenticationService.getAuthenticationToken()}`,
      }}
      fieldName="file"
      extraFormData={{ department: announcement.department }}
      onUploadProgress={(p) => console.log("Upload progress:", p)}
      setIsAnnouncement={(val) =>
        setAnnouncement((prev) => ({ ...prev, isActive: val }))
      }
      setAnnouncementDepartment={(val) =>
        setAnnouncement((prev) => ({ ...prev, department: val }))
      }
    />
  );

  const isAmbientConnecting =
    callType.isAmbient && !Boolean(callStateRef.current.roomId);

  return (
    <MDBox p={0}>
      <div id="players"></div>
      <Card
        sx={{
          p: 1,
          display: "flex",
          flexDirection: "column",
          height: compactMode ? "100%" : "82vh",
          background: darkMode
            ? "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)"
            : "linear-gradient(135deg, #f5f5f5 0%, #e3f2fd 50%, #f0f4ff 100%)",
          boxShadow: darkMode
            ? "0 20px 60px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.1)"
            : "0 20px 60px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.8)",
          border: darkMode
            ? "1px solid rgba(66, 165, 245, 0.2)"
            : "1px solid rgba(33, 150, 243, 0.15)",
          borderRadius: 4,
          transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            background: darkMode
              ? "radial-gradient(circle at 20% 50%, rgba(66, 165, 245, 0.03) 0%, transparent 50%)"
              : "radial-gradient(circle at 80% 20%, rgba(33, 150, 243, 0.02) 0%, transparent 50%)",
            pointerEvents: "none",
            borderRadius: "inherit",
          },
        }}
      >
        {!announcement.isActive ? (
          <>
            <PhoneHeader
              connectionState={connectionState}
              callListCount={callList.length}
              onOpenCallList={() => setIsCallListOpen(true)}
              pttStatus={pttStatus}
              callType={callType}
              callActive={!!callStateRef.current.roomId}
              callStreamId={callStateRef.current.streamId}
              darkMode={darkMode}
              compactMode={compactMode}
            />

            {!isVideoMaximized && (
              <VideoPanel
                remoteStreams={remoteStreams}
                callStreamId={callStateRef.current.streamId}
                darkMode={darkMode}
                videoPanelHeight={videoPanelHeight}
                onResizeStart={startVideoResize}
                showMaximizeControl={
                  allowVideoPopup &&
                  compactMode &&
                  !!callStateRef.current.roomId
                }
                isMaximized={false}
                onToggleMaximize={() => setIsVideoMaximized(true)}
              />
            )}

            {!compactMode && callTimer !== "00:00:00" && callType.isP2P && (
              <MDTypography
                variant="h5"
                sx={{
                  textAlign: "center",
                  fontWeight: 700,
                  color: darkMode ? "#90caf9" : "#1976d2",
                  py: 1,
                  letterSpacing: "0.5px",
                  fontSize: "22px",
                }}
              >
                {callTimer}
              </MDTypography>
            )}

            <Box sx={{ p: 0 }}>
              {compactMode &&
                !callStateRef.current.roomId &&
                !isAmbientConnecting && (
                <Box sx={{ px: 1, pb: 1, pt: 0.75 }}>
                  <MDInput
                    fullWidth
                    value={dialValue}
                    onChange={(e) =>
                      setDialValue(e.target.value.replace(/[^\d*#]/g, ""))
                    }
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        dialCall(false);
                      }
                    }}
                    placeholder="Enter number to dial"
                    inputProps={{ maxLength: 32 }}
                  />
                </Box>
              )}

              {!compactMode &&
                !callStateRef.current.roomId &&
                !isAmbientConnecting &&
                callList.length === 0 && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <DialPad
                      dialValue={dialValue}
                      onChangeDial={(e) =>
                        setDialValue(e.target.value.replace(/[^\d*#]/g, ""))
                      }
                      onAppend={appendDial}
                      darkMode={darkMode}
                    />
                  </>
                )}
            </Box>

            <Divider sx={{ my: 2 }} />

            <CallControls
              darkMode={darkMode}
              callActive={
                !!callStateRef.current.roomId || isAmbientConnecting
              }
              dialValue={dialValue}
              onDialAudio={() => dialCall(false)}
              onDialVideo={() => dialCall(true)}
              onClearDial={clearDial}
              isMuted={isMuted}
              onToggleMic={handlePTT}
              canToggleVideo={callType.isP2P && callType.isVideoCall}
              isVideoEnabled={isVideoEnabled}
              onToggleVideo={toggleVideo}
              callType={callType}
              onLeaveConference={leaveConference}
              onDisconnectCall={() => disconnectCall()}
              onDisconnectAmbient={disconnectAmbientCall}
            />

            {!compactMode && callList.length > 0 && (
              <Box
                sx={{
                  mt: 1.5,
                  px: 1,
                  pb: 0.5,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.8,
                  maxHeight: "auto",
                  overflowY: "auto",
                  "&::-webkit-scrollbar": {
                    width: "5px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "transparent",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: darkMode
                      ? "rgba(66, 165, 245, 0.4)"
                      : "rgba(33, 150, 243, 0.4)",
                    borderRadius: "3px",
                    "&:hover": {
                      background: darkMode
                        ? "rgba(66, 165, 245, 0.6)"
                        : "rgba(33, 150, 243, 0.6)",
                    },
                  },
                  scrollbarWidth: "thin",
                  scrollbarColor: darkMode
                    ? "rgba(66, 165, 245, 0.4) transparent"
                    : "rgba(33, 150, 243, 0.4) transparent",
                }}
              >
                <MDTypography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    letterSpacing: "0.4px",
                    color: darkMode ? "#90caf9" : "#1565c0",
                    px: 0.4,
                  }}
                >
                  Waiting Calls ({callList.length})
                </MDTypography>

                {callList.map((call) => (
                  <Box
                    key={call.roomId}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      px: 1,
                      py: 0.8,
                      borderRadius: 1.5,
                      border: `1px solid ${
                        darkMode
                          ? "rgba(66, 165, 245, 0.35)"
                          : "rgba(33, 150, 243, 0.22)"
                      }`,
                      background: darkMode
                        ? "rgba(12, 27, 47, 0.55)"
                        : "rgba(227, 242, 253, 0.8)",
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <MDTypography
                        variant="caption"
                        sx={{
                          display: "block",
                          fontWeight: 700,
                          color: call.isSOS ? "#ef5350" : "#42a5f5",
                          lineHeight: 1.1,
                        }}
                      >
                        {call.isSOS
                          ? "SOS"
                          : call.isVideo
                            ? "Private Video"
                            : "Private Audio"}
                      </MDTypography>
                      <MDTypography
                        variant="caption"
                        sx={{
                          display: "block",
                          maxWidth: 150,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          opacity: 0.9,
                        }}
                      >
                        {call.sender}
                      </MDTypography>
                    </Box>

                    <Box sx={{ display: "flex", gap: 0.6, flexShrink: 0 }}>
                      <MDButton
                        size="small"
                        color="success"
                        variant="contained"
                        sx={{
                          minWidth: 58,
                          px: 1,
                          py: 0.3,
                          fontSize: "0.64rem",
                          lineHeight: 1.1,
                        }}
                        onClick={() => dialWaitingCall(call)}
                      >
                        Join
                      </MDButton>
                      <MDButton
                        size="small"
                        color="error"
                        variant="contained"
                        sx={{
                          minWidth: 58,
                          px: 1,
                          py: 0.3,
                          fontSize: "0.64rem",
                          lineHeight: 1.1,
                        }}
                        onClick={() => disconnectWaitingCall(call)}
                      >
                        Reject
                      </MDButton>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}

            {!compactMode && (
              <CallListDialog
                open={isCallListOpen}
                onClose={() => setIsCallListOpen(false)}
                callList={callList}
                darkMode={darkMode}
                onJoin={dialWaitingCall}
                onReject={disconnectWaitingCall}
              />
            )}
          </>
        ) : (
          announcementRecorder
        )}
      </Card>

      {allowVideoPopup && (
        <Dialog
          open={isVideoMaximized}
          onClose={() => setIsVideoMaximized(false)}
          hideBackdrop
          maxWidth={false}
          PaperComponent={DraggablePaper}
          sx={{
            "& .MuiPaper-root": {
              width: {
                xs: "92vw",
                sm: 560,
              },
              maxWidth: "92vw",
              border: (theme) => `1px solid ${theme.palette.divider}`,
            },
          }}
        >
          <DialogTitle
            id="draggable-video-card-title"
            className="draggable-video-card-title"
            sx={{
              py: 1,
              px: 1.5,
              cursor: "move",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            }}
          >
            <MDTypography variant="button" fontWeight="medium">
              Live Video Panel
            </MDTypography>
            <IconButton size="small" onClick={() => setIsVideoMaximized(false)}>
              <FullscreenExitIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 1.5 }}>
            <VideoPanel
              remoteStreams={remoteStreams}
              callStreamId={callStateRef.current.streamId}
              darkMode={darkMode}
              videoPanelHeight={Math.max(videoPanelHeight, 300)}
              onResizeStart={startVideoResize}
              showMaximizeControl
              isMaximized
              onToggleMaximize={() => setIsVideoMaximized(false)}
            />
          </DialogContent>
        </Dialog>
      )}

      <CTLNotification notify={notify} setNotify={setNotify} />
    </MDBox>
  );
});

export default WebRTCPhone;
